// src/views/CalendarView.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import { add, isBefore, startOfDay } from 'date-fns';
import { projectRecurringOccurrences } from '../utils/recurrence'; // r1-rec-cal-route-b8d3f5a1
import { flattenAppearances, hasCrewSchedule, toDateKey, makeAppearanceId } from '../utils/crewSchedule'; // r3-cs-calfan-4e7b1a90
import { getJobDateSpan, formatJobNumber } from '../utils/formatters'; // r3-cs-calfanwrite-2c9f6b31; formatJobNumber: jobnumdisp-6a1e9c34
import { resolveArrivalWindowKey, getJobArrivalWindowShortLabel } from '../utils/arrivalWindows'; // calwin-8d4f1b52

import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

import { matchesJobSearch, matchesQuoteSearch } from '../utils/jobSearch'; // calpol-9c4e7b23; matchesQuoteSearch: qfupcal-6e3b9d47
// qfupcal-6e3b9d47 — SEAM 7B. isApprovedStatus/isDeclinedStatus already fold the
// legacy 'Accepted' value, so the openness test below writes no new predicate.
// resolveQuoteSalesPerson has existed since Seam 0 with no web consumer; this is
// its first one.
import { isApprovedStatus, isDeclinedStatus, QUOTE_STATUSES, resolveQuoteSalesPerson, quoteFollowUpOverdueDays, isQuoteArchived } from '../utils/quoteSchema'; // isQuoteArchived: estdel-f9ac8ba8

import { db } from '../firebase/config';
import { collection, query, onSnapshot, where } from 'firebase/firestore';
// calpol-9c4e7b23 — `Map` is aliased to MapIcon deliberately. Imported bare, it
// SHADOWS the global Map constructor for the whole module, so `new Map()`
// anywhere in this file resolves to a React component and throws
// "…is not a constructor" at runtime. The import is not new — what was new is
// this seam's first use of a real Map (visitCountByDay) in a file that had
// silently held that shadow since the Route This Day button was added.
import { Plus, Map as MapIcon, Search, XCircle, PhoneCall } from 'lucide-react';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { can, PERMISSIONS } from '../utils/permissions';
import { buildReassignment, buildReassignDialog, afterDropNotice, seedForSlot } from '../utils/calendarUnassign'; // calclar-5e2a8d14
import { useNavigate, useLocation } from 'react-router-dom';

const locales = {
  'en-US': enUS,
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// calwin-8d4f1b52 — make the library's event sort a STABLE IDENTITY so the order
// we build below is the order rendered.
//
// WHY this is necessary rather than just sorting our array: the all-day row
// re-sorts whatever it is handed (react-big-calendar TimeGrid.js:297 →
// eventLevels.sortEvents → localizer.sortEvents), so a pre-sort is otherwise
// thrown away. And the default comparator CANNOT produce the order we need: it
// orders same-day events by duration DESCENDING before start time, and a Morning
// window and an All-day window both start at 08:00 (arrivalWindows.js:40-43), so
// no arrangement of real timestamps separates them.
//
// WHY not feed the rank through a hook instead: the only sort hook the library
// exposes receives just {start, end, allDay} (eventLevels.js:121-135) — it never
// sees the event, so a rank attached to the event is invisible to it. Encoding
// the rank INTO allDay would make one field mean two things, which is how a
// schema starts lying. So: neutralise the sort, and own the order explicitly.
//
// `dateFnsLocalizer` builds a fixed spec and drops unknown keys, so this is
// assigned onto the instance (DateLocalizer sets sortEvents as an own property,
// localizer.js:152). Array.prototype.sort is stable, so returning 0 preserves
// input order exactly.
//
// SCOPE OF EFFECT, checked: the all-day row (what we want) and month's
// within-group ordering (Month.js:65) — month keeps its own multi-day-first
// partition regardless. The time grid is unaffected because its layout algorithm
// re-sorts by start time internally (layout-algorithms/overlap.js:117), and the
// agenda view is unaffected because it sorts by start itself (Agenda.js:133).
localizer.sortEvents = () => 0;

const DnDCalendar = withDragAndDrop(Calendar);

// calwin-8d4f1b52 — the "Arrival window" lane.
//
// ~95% of production jobs carry an arrival window rather than a clock time, and
// the save path collapses every one of them onto the window's START hour
// (JobFormModal.js:2155-2164 — the window's end is never used). So six "Morning"
// jobs became six slivers stacked on 08:00. These jobs do not belong in the time
// grid at all: they have no start time, they have a promise.
const UNTIMED_RANK_BY_WINDOW = { morning: 0, afternoon: 1, allday: 2 };
const UNTIMED_RANK_TIME_OFF = 3;
const TIMED_RANK = -1; // timed events sort ahead of untimed ones within a day
// qfupcal-6e3b9d47 — quote follow-ups sort LAST in the lane, below Time Off.
// Every other entry in this lane is a promise made to a customer (an arrival
// window) or someone's absence; a follow-up is an internal reminder to make a
// phone call. Customer promises stay on top.
const UNTIMED_RANK_FOLLOW_UP = 4;

// An entry has no real clock time when the dispatcher chose an arrival window,
// when it is a Time Off block, or when it is a multi-day all-day job.
const isUntimedJob = (job) => !!job && (
    job.schedulingMode === 'window'
    || job.eventType === 'Time Off'
    || job.isAllDay === true
);

// Lane order: Morning → Afternoon → All day → Time Off. 'anytime' is a legacy
// stored value that normalises to 'allday' (arrivalWindows.js:47), so it ranks
// with All day rather than forming a fourth bucket.
const untimedRankForJob = (job) => {
    if (!job) return UNTIMED_RANK_TIME_OFF;
    if (job.eventType === 'Time Off' || job.isAllDay === true) return UNTIMED_RANK_TIME_OFF;
    const key = resolveArrivalWindowKey(job.arrivalWindow, 'allday');
    const rank = UNTIMED_RANK_BY_WINDOW[key];
    return rank === undefined ? UNTIMED_RANK_BY_WINDOW.allday : rank;
};

// ═══════════════════════════════════════════════════════════════════════════
// qfupcal-6e3b9d47 — SEAM 7B: quote follow-ups on the calendar.
//
// GOVERNED BY A4.1: a follow-up is drawn DIRECTLY FROM THE QUOTE. No job
// document is ever created, and this seam writes NOTHING — it adds one read
// listener and a fourth event shape. A job carrying quoteId is what moves a
// quote to Won (convertedToJobId); that path is untouched.
//
// 🛑 THE DISCRIMINATOR IS `entryKind`, AND `resource` IS DELIBERATELY NULL.
// Setting resource: null is load-bearing, not tidiness. Every one of this
// file's .resource reads uses optional chaining — including
// `event?.resource?.assignedTo?.[0]` in resolveEntryColor — so a follow-up
// event falls through every job-shaped branch as `undefined` instead of
// throwing. And handleSelectEvent's own guard is `if (event.resource)`, so a
// follow-up click does NOTHING until the explicit branch below is reached.
// The failure mode of a site we missed is therefore inert, never "opened the
// job editor on a quote document".
const ENTRY_KIND_FOLLOW_UP = 'quoteFollowUp';
const isQuoteFollowUpEntry = (event) => event?.entryKind === ENTRY_KIND_FOLLOW_UP;

// calreturn-7b2e9d41 — the views react-big-calendar is actually given here. There is
// no `views=` prop and no custom toolbar on the Calendar below, so these four ARE the
// library defaults. Used to whitelist ?view=, so a hand-edited or stale URL can never
// hand rbc a view name it cannot render.
const CALENDAR_VIEWS = ['month', 'week', 'day', 'agenda'];

// The one place the 768px mobile breakpoint is defined. Below it the calendar is shown
// as an agenda regardless of what the URL says — see the resize effect for why that is
// a display decision only and never a write.
const isNarrowScreen = () => window.innerWidth < 768;

// Returns the user's CHOSEN view from the URL, or null if they have not chosen one.
// null is meaningful: it is what keeps ?view= out of the URL until a real choice is made.
const parseViewParam = (loc) => {
    const raw = new URLSearchParams(loc.search).get('view');
    return CALENDAR_VIEWS.includes(raw) ? raw : null;
};

// Slate on time, amber overdue. The amber is #b45309 (amber-700) — the exact
// colour of the "Was due" pill on the estimate page (QuoteFollowUpStrip.js),
// so overdue means the same thing in both places. Never a person colour: a
// follow-up is a different species from a visit, and borrowing the assignee
// palette would say otherwise.
const FOLLOW_UP_COLOR = '#475569';
const FOLLOW_UP_COLOR_OVERDUE = '#b45309';

// Which quotes produce an entry. Reuses the reminder ladder's own exclusions
// (functions/index.js — convertedToJobId, then decided statuses), with two
// DELIBERATE departures that must not be "fixed" into line with it:
//
//   • SNOOZED QUOTES DO SHOW. The ladder skips them because it sends the
//     customer an email; this is the salesperson's own list, and A4.1 says a
//     snoozed follow-up appears at its wake-up date with the reason.
//   • EXPIRED ESTIMATES DO SHOW. Same reason — a calendar entry nags nobody.
//     Hiding it would make outstanding work disappear.
//
// Note there is only ever ONE date on a quote: snoozing MOVES nextFollowUpAt to
// the wake-up instant rather than adding a second field (quoteFollowUp.js), so
// "show it at the wake-up date" needs no code at all.
const quoteHasOpenFollowUp = (quote) => {
    if (!quote?.nextFollowUpAt) return false;
    if (quote.convertedToJobId) return false;          // it became a job; never chase it
    if (isApprovedStatus(quote.status)) return false;  // decided (folds legacy 'Accepted')
    if (isDeclinedStatus(quote.status)) return false;  // decided
    if (quote.status === QUOTE_STATUSES.AWAITING_DEPOSIT) return false;
    return true;
};

// Overdue is derived exactly as the estimate page derives it — because since
// qattn-8c4e1f92 it IS the estimate page's derivation, not a second copy of it.
// This wrapper keeps the call shape the two sites below already use and adds
// the one thing the old local version got wrong: the day boundary is now the
// ORG's midnight, not the viewer's, so a salesperson working from a different
// province sees the same amber as the office does.
const isFollowUpOverdue = (nextAt, snoozedUntil, now, timeZone) =>
    quoteFollowUpOverdueDays({ nextFollowUpAt: nextAt, snoozedUntil }, { now, timeZone }) !== null;

// calpol-9c4e7b23 — the WHEN segment that leads a month entry.
//
// WHY MONTH NEEDED ONE AT ALL: react-big-calendar renders only the TITLE in a
// month cell (EventCell.js:54-67) — the `rbc-event-label` that shows a time is a
// time-grid thing and month never draws it. So a month entry has never said when
// the job is, and on a day with six jobs you had to open one to find out.
//
// A windowed job shows its window, not a clock time, for the same reason the lane
// exists: 08:00 on a "Morning" job is the save path's placeholder
// (JobFormModal.js:2155-2164), not a promise anyone made to the customer.
//
// Format is `h:mm` with no am/pm — "8:30", "2:00" — chosen to keep the segment
// short in a ~150px cell. Field-service days are daytime, so the ambiguity is
// theoretical; if it reads wrong in use, 'h:mma' is a one-word change here and
// nowhere else.
const entryWhenLabel = (start, untimed, windowLabel) => {
    // An untimed entry with no resolvable window is a multi-day / isAllDay job
    // (getArrivalWindowShortLabel returns '' for a null window,
    // arrivalWindows.js:130-135), so name it rather than leading with a blank.
    if (untimed) return windowLabel || 'All day';
    if (!(start instanceof Date) || Number.isNaN(start.getTime())) return '';
    return format(start, 'h:mm');
};

// Total order over every event, since we now own ordering outright (see the
// localizer.sortEvents note above). Rank first, then start/end/title so the
// result is deterministic rather than dependent on Firestore document order.
//
// calwin-8d4f1b52-fix — this deliberately does NOT sort by calendar day first.
// It used to, and that is what made a five-day vacation render ABOVE the Morning
// entries on 2026-09-22: a multi-day entry's `start` is an EARLIER day than the
// day being viewed, so a day-first term sorted it ahead of everything in the
// lane. The library segments events into days by itself, so the array order only
// decides stacking within a row — sorting by day bought nothing and cost the
// agreed order. Ranking first gives Morning → Afternoon → All day → Time Off on
// every day, including days a multi-day entry merely passes through, and events
// of the same rank still fall chronologically because `start` carries the date.
const compareCalendarEvents = (a, b) => {
    if (a.untimedRank !== b.untimedRank) return a.untimedRank - b.untimedRank;
    const startDiff = a.start.getTime() - b.start.getTime();
    if (startDiff) return startDiff;
    const endDiff = a.end.getTime() - b.end.getTime();
    if (endDiff) return endDiff;
    return String(a.title).localeCompare(String(b.title));
};

// calres-7f2b9d41 — SEAM 3: day view organised by team member.
//
// One resource column per ticked team member, in the team filter's own order,
// plus a synthetic "Unassigned" column last. Day view only.
//
// 🛑 THE TRAP THIS SENTINEL EXISTS FOR: an event whose resourceId matches none of
// the supplied resources is filed under an internal NONE key that the library
// never iterates (react-big-calendar utils/Resources.js:26), so it renders
// NOWHERE, silently, with no error. Every event must therefore carry a resourceId
// that exists in the resources array. That is not a tidiness point: 66 of GC
// Septic's 74 jobs and 42 of the test org's 271 have no assignee at all, so
// without this column GC Septic's day view would simply be empty.
const UNASSIGNED_RESOURCE_ID = '__unassigned__';

// Every team member a job touches, across all four assignment shapes that are in
// live use in the same org (census: 163 jobs with one assignedTo, 58 with two or
// more, 47 with a routeGroupId, 10 with line-item crews[]).
const resolveJobMemberIds = (job, routeGroupList) => {
    const ids = new Set();
    const addAll = (list) => (Array.isArray(list) ? list : []).forEach(id => { if (id) ids.add(id); });
    const addGroup = (groupId) => {
        if (!groupId) return;
        addAll(routeGroupList.find(g => g.id === groupId)?.memberIds);
    };
    addAll(job?.assignedTo);
    addAll(job?.assignedGroupMemberIds);
    addGroup(job?.routeGroupId);
    if (Array.isArray(job?.crews)) job.crews.forEach(c => addGroup(c?.crewGroupId));
    return ids;
};

// Narrow a job's people to the columns actually on screen, and fall back to the
// Unassigned column otherwise. NEVER returns an empty array: the library treats a
// truthy-but-empty resourceId as "belongs to no column" and drops the event
// (Resources.js:25-31 iterates the array, so [] files it nowhere).
// A single id is returned bare and several as an array, which is what makes a
// multi-assignee or crew job appear in EVERY relevant person's column — the
// library fans an array out natively, so this seam writes no fan-out code.
const toResourceId = (memberIds, visibleIdSet) => {
    const matched = [...memberIds].filter(id => visibleIdSet.has(id));
    if (matched.length === 0) return UNASSIGNED_RESOURCE_ID;
    return matched.length === 1 ? matched[0] : matched;
};

// calres3b-6e4a2f81 — SEAM 3B: reassign by drag.
//
// WHAT IS AUTHORITATIVE. `assignedTo` is NOT. For a Job, JobFormModal recomputes it
// from the labour rows on every save (`mergedLaborEntries.map(e => e.userId)`,
// JobFormModal.js:2182), so `jobCosting.laborEntries` is the real assignment record
// and `assignedTo` / `assignedToNames` are index-aligned mirrors of it. A reassign
// that wrote `assignedTo` alone would be silently reverted by the next form save —
// the failure class functions/scripts/detectAssignmentWipe.js exists to catch. So
// every write here edits the labour rows FIRST and derives the two arrays from them.
//
// RATES ARE NOT RESOLVED HERE. A new row carries `rate: null`; the server trigger
// stampJobLaborRates (functions/index.js:22738) resolves it through getLaborCostRate.
// Never seed from `hourlyRate` — that is the payroll wage, not the job-cost rate
// (the same warning as JobFormModal.js:1626-1628). Rows that stay keep their frozen
// rate, because stampJobLaborRates only re-resolves uids absent from the BEFORE
// snapshot, so nobody else's cost moves.
//
// 🛑 WHY THE SOURCE COLUMN IS SOMETIMES ASKED FOR RATHER THAN DERIVED. The library
// tells us the DROP target only. `sourceResource` is dead code — EventWrapper builds
// an `extendedEvent`, sets it on that copy, then calls onBeginAction with the
// ORIGINAL event (addons/dragAndDrop/EventWrapper.js:49-51) — and `onDragStart`
// passes only `{event, action, direction}` (withDragAndDrop.js:41-46). A job with
// several assignees is legitimately rendered in several columns at once from ONE
// event object, so "which column did this leave" has no answer in the data. Reading
// it off DOM column order would be a silent wrong-person removal the moment the
// library's layout changed, so when the event owns more than one visible column the
// dialog asks. One column → no question.

// A crew-assigned job refuses the drag (founder decision D2): the fan-out means it is
// visibly present in several columns at once, so there is no single person to move it
// away from. 64 of 408 production jobs are this shape.
const isCrewAssigned = (job) =>
    !!job?.routeGroupId || (Array.isArray(job?.crews) && job.crews.length > 0);

// calclar-5e2a8d14 — hasClockedRow / baseLaborRows / buildReassignment moved VERBATIM to
// utils/calendarUnassign.js (plus the "Remove all" branch), so the dialog's wording is computed by
// the very function the write uses. Regression-tested against the copy that lived here.

// calwin-8d4f1b52-fix — FINISHED work is read-only on the calendar.
// Dragging a Completed job rewrote WHEN finished work happened, and resizing it
// rewrote how long it took. Neither is a scheduling decision, so neither belongs
// to a drag gesture. Reads `resource.status`, which every entry kind carries: a
// plain job, a materialised recurring instance, and a per-crew appearance (whose
// resource is the parent job, so a completed crew job's blocks lock too).
const isFinishedEntry = (event) => {
    const status = event?.resource?.status;
    return status === 'Completed' || status === 'Canceled';
};

// calwin-8d4f1b52-fix — the lane's gutter label.
// There is NO built-in all-day label in day/week view: the gutter cell beside the
// all-day row renders only `components.timeGutterHeader`, which is undefined by
// default (react-big-calendar TimeGridHeader.js:146-153). `messages.allDay` is
// read only by the agenda view (Agenda.js:80) and by an event's own label when it
// spans a whole day (DayColumn.js:77) — never by that gutter. So seam 2's
// `messages` override could never have shown there. This is the actual slot.
const ArrivalWindowGutterLabel = () => (
    <span className="block text-[10px] leading-tight text-gray-500 font-medium text-right pr-1">
        Arrival<br />window
    </span>
);

// calwin-8d4f1b52-fix — agenda's Time column for a lane entry.
// Agenda falls back to `messages.allDay` for anything our allDayAccessor calls
// untimed (Agenda.js:80-82), so without this a Morning job would read "All Day"
// there. Phones default to the agenda view, so this is the one every technician
// sees. Shows the window short label when there is one, and leaves the library's
// own label alone otherwise (a vacation still reads "All Day", correctly).
const AgendaTimeLabel = ({ event, label }) => {
    // qfupcal-6e3b9d47 — a quote has no arrival window to resolve. A date-only
    // follow-up says so in words; a timed one keeps the library's clock label.
    if (isQuoteFollowUpEntry(event)) {
        return <span>{event.isUntimed ? 'Follow up' : label}</span>;
    }
    const windowLabel = event?.resource?.eventType === 'Time Off'
        ? ''
        : getJobArrivalWindowShortLabel(event?.resource);
    return <span>{(event?.isUntimed && windowLabel) ? windowLabel : label}</span>;
};

// calpol-9c4e7b23 — the per-day visit count in the month date header.
//
// `components.month.dateHeader` is handed {label, date, drilldownView,
// isOffRange, onDrillDown} and NOT the events (Month.js:95-119), so the count has
// to be closed over — see the useMemo that builds the components object.
//
// 🛑 The drill-down button is reproduced rather than dropped. Clicking a date
// number opens that day (DateHeader.js) and that is live behaviour; a custom
// dateHeader that renders a bare label would silently remove it.
//
// `.rbc-date-cell` is `text-align: right` with `padding-right: 5px`
// (react-big-calendar.css:402-409), hence the flex wrapper: the count sits left,
// the date number stays exactly where it has always been on the right.
const MonthDateHeader = ({ label, drilldownView, onDrillDown, count }) => (
    <span className="flex items-center justify-between w-full">
        <span className="pl-1 text-[10px] font-medium text-gray-400 leading-none">
            {count > 0 ? count : ''}
        </span>
        {drilldownView
            ? <button type="button" className="rbc-button-link" onClick={onDrillDown}>{label}</button>
            : <span>{label}</span>}
    </span>
);

// calpol-9c4e7b23 — agenda's event cell.
//
// WHY: eventStyleGetter painted the saturated per-person colour as the agenda
// ROW's background. In month/week/day that colour lands on a small block; in
// agenda the rows are full-width, so a day read as a stack of coloured stripes
// rather than as a list. The colour still carries real information (whose job it
// is), so it is kept — moved off the row and onto a dot at the head of the title.
//
// The dot is drawn INSIDE the event cell deliberately. The alternative, a
// `border-left` on the <tr>, sits on a table row inside a border-collapse table
// (react-big-calendar.css:480-483), where the library's own `td + td` borders
// (:493-494) take part in the same border-conflict resolution — so the bar's
// appearance would depend on rules we do not own. Nothing competes for the
// inside of the cell.
//
// `flex-shrink-0` on the dot and `min-w-0` on the title keep the dot from being
// squeezed away when a long title truncates on a phone.
const AgendaEventContent = ({ title, color }) => (
    <span className="flex items-center gap-2 min-w-0">
        <span
            className="inline-block h-2 w-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: color }}
            aria-hidden="true"
        />
        <span className="min-w-0">{title}</span>
    </span>
);

// calres-7f2b9d41-layout — the resource day view's column geometry.
//
// Three founder-reported faults, all caused by the library's OWN resources CSS
// sizing the header columns and the body columns by DIFFERENT rules:
//
//   1. A long title widened its column. `.rbc-time-view-resources
//      .rbc-time-header-content` ships `min-width: auto` with `flex: 1 0 0`
//      (react-big-calendar.css:638-644). On a flex item, `min-width: auto` means
//      "never shrink below your content", so one long unbroken title forced that
//      header column wider and pushed the rest right.
//   2. The lane stopped lining up with the grid. Header columns grow from content
//      (above) while body columns are `flex: 1 1 0` with `min-width: 140px`
//      (css:649-651) — two different sizing rules over the same column count, so
//      they desynchronise the moment any title is long.
//   3. The right-hand columns were cut off rather than reachable, because
//      `.rbc-time-view-resources .rbc-time-header { overflow: hidden }` (css:635)
//      CLIPS the header, while `.rbc-time-content` has `overflow-y: auto` and
//      therefore a computed `overflow-x: auto` — so the body could scroll
//      sideways on its own and the header could not follow.
//
// The fix is to size header and body columns from the SAME fixed rule and let one
// outer container scroll the whole thing. Growing columns to fill spare width was
// deliberately rejected: growth depends on each flex container's available space,
// and the header and body containers do not always agree about that (the body's
// vertical scrollbar is inside its own box), which is precisely the
// misalignment being fixed. Fixed columns trade some whitespace at low column
// counts for alignment that cannot drift.
//
// 180px is chosen to fit "Morning · <customer>" truncated but still readable, and
// to keep 7 columns on screen on a 1366-wide laptop before scrolling. At the real
// maximum — 8 people plus Unassigned — 9 × 180 + the gutter is ~1690px, so it
// scrolls, as one unit, which is the requirement rather than a compromise.
//
// The COLUMN rules below are scoped entirely to `.rbc-time-view-resources`, a
// class the library adds only when `resources` is passed (TimeGrid.js:327) — i.e.
// day view only, so week, month and agenda cannot be reached by them. The
// `.rbc-overlay` rules that follow are deliberately UNSCOPED, because the "+N
// more" popover has to look the same in day, week and month.
const CALENDAR_CSS = `
/* calmore-2d8f4b17 — the "+N more" popover.
   Turning on the library's "popup" prop replaces a dead drill-down with a real
   overlay, but two things about it need correcting here:

   1. WIDTH. TimeGrid hardcodes "width: '200px'" into the overlay's position
      (TimeGrid.js:82-84) while Month passes no width at all (Month.js:166-170) —
      so the same control would be 200px in day/week and content-width in month.
      200px also truncates the titles this calendar actually has (66 characters on
      2026-07-13). Overridden to one comfortable, consistent size in every view.
   2. STACKING. ".rbc-overlay" is z-index 5 (css:446-454) but the resource day
      view's sticky time gutter is z-index 10 (css:626-633), so a popover opening
      over the gutter would slide UNDERNEATH it.

   Entries inside the popover deliberately WRAP rather than ellipsise: the library's
   ellipsis rule is scoped to ".rbc-row-segment .rbc-event-content" and the popup
   renders its EventCells outside that, so full titles show — which is the whole
   point of opening it. A tall list scrolls inside the popover instead of being
   capped, so "every entry" really means every entry. */
.rbc-overlay {
  width: auto !important;
  min-width: 240px;
  max-width: 380px;
  max-height: 60vh;
  overflow-y: auto;
  z-index: 20;
}
.rbc-overlay .rbc-event { white-space: normal; }
.rbc-overlay .rbc-event-content {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

.rbc-time-view-resources { --spatchy-res-col: 180px; width: max-content; min-width: 100%; }
.spatchy-res-day .rbc-calendar { width: max-content; min-width: 100%; }

/* One rule for BOTH the header column (name + arrival-window lane) and the body
   column (time grid), so they are always the same width and always aligned. */
.rbc-time-view-resources .rbc-time-header-content,
.rbc-time-view-resources .rbc-time-content > .rbc-day-slot {
  flex: 0 0 var(--spatchy-res-col);
  width: var(--spatchy-res-col);
  min-width: var(--spatchy-res-col);
  max-width: var(--spatchy-res-col);
}

/* Reach the right-hand columns instead of clipping them: the header stops hiding
   its overflow, the body stops scrolling sideways by itself, and the single outer
   wrapper (which already has overflow-x-auto) scrolls headers, lane and grid
   together. The library's sticky time gutter keeps working and now pins itself
   while scrolling, because that wrapper is its scroll container. */
.rbc-time-view-resources .rbc-time-header { overflow: visible; }
.rbc-time-view-resources .rbc-time-content { overflow-x: hidden; }

/* Truncate rather than push. The lane already ellipsises (css:66-71); the time
   grid does not — it ships word-wrap: break-word (css:612-618) — and a resource
   name long enough to widen its own header was the third way a column grew.
   The full text is already available on hover: both the lane and the grid set a
   title attribute from tooltipAccessor, which defaults to the event title
   (EventCell.js:57, TimeGridEvent.js:68). */
.rbc-time-view-resources .rbc-day-slot .rbc-event-content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-wrap: normal;
}
.rbc-time-view-resources .rbc-event-label,
.rbc-time-view-resources .rbc-row-resource .rbc-header {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* calpol-9c4e7b23 — stop the agenda Time column lower-casing its own contents.
   This is the LIBRARY's rule, not ours: react-big-calendar.css sets
   text-transform: lowercase on .rbc-agenda-time-cell in TWO places (:488-492 and
   :511-513), which is why our capitalised "Morning" rendered as "morning" while
   the title beside it stayed capitalised, and why clock times read "8:00 am".
   The selector below matches the library's more specific of the two (0,3,1) so it
   wins on specificity rather than relying on this <style> tag merely coming later
   in the document than the imported stylesheet. Result: "8:00 AM" and "Morning",
   consistent with every other label on the row. */
.rbc-agenda-view table.rbc-agenda-table .rbc-agenda-time-cell {
  text-transform: none;
}

/* calpol-9c4e7b23 — agenda rows read as a LIST, not as coloured stripes.
   The per-person colour moves to a dot inside the cell (AgendaEventContent), so
   the row itself needs no background. eventStyleGetter already returns a
   transparent background and dark text for agenda; this is the hover affordance
   that replaces "the whole row is a solid colour" as the thing that shows you
   which row you are on. */
.rbc-agenda-view table.rbc-agenda-table tbody > tr:hover > td {
  background-color: #f9fafb;
}
`;

const EMPLOYEE_COLORS = ['#3174ad', '#ff9f00', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

const getColorForEmployee = (employeeId, employeeList) => {
    if (!employeeId || !employeeList || employeeList.length === 0) {
        return EMPLOYEE_COLORS[0];
    }
    const index = employeeList.findIndex(e => e.id === employeeId);
    if (index === -1) {
        return EMPLOYEE_COLORS[0];
    }
    return EMPLOYEE_COLORS[index % EMPLOYEE_COLORS.length];
};

// r3-cs-calfan-4e7b1a90 — per-crew color for continuous per-crew appearance events.
// Mirrors getColorForEmployee but indexes the crew's position in routeGroups, so a
// given crew keeps ONE stable color across the whole calendar (a dispatcher learns
// "plow = blue" and it holds every day / every job). Falls back to the first palette
// color when the crew can't be located in routeGroups.
const getColorForCrew = (crewGroupId, routeGroupList) => {
    if (!crewGroupId || !routeGroupList || routeGroupList.length === 0) {
        return EMPLOYEE_COLORS[0];
    }
    const index = routeGroupList.findIndex(g => g.id === crewGroupId);
    if (index === -1) {
        return EMPLOYEE_COLORS[0];
    }
    return EMPLOYEE_COLORS[index % EMPLOYEE_COLORS.length];
};

// calpol-9c4e7b23 — the ONE answer to "what colour is this entry".
//
// WHY IT WAS EXTRACTED. Agenda stops painting this colour as a full-width row
// background (it made a day read as stripes) and starts drawing it as a dot
// INSIDE the row instead. The dot must be the colour the row would have had, so
// two different pieces of code now need the same answer — and the moment that
// answer lives in two places they drift, and a Completed job gets a green row in
// month and a blue dot in agenda.
//
// 🛑 THIS IS A PURE EXTRACTION. Every branch below returns exactly the hex
// eventStyleGetter assigned at the same point before this seam, in the same
// order, under the same conditions. eventStyleGetter keeps its own branch
// structure byte-for-byte and now reads the background from here; opacity,
// text-decoration and the Follow-Up border stay with it, because those are not
// colour. If you change a hex here you change month, week, day AND agenda.
// Takes only `employeeList`: a crew appearance's colour is already resolved onto
// the event as `crewColor` when the event is built (getColorForCrew, above), so
// routeGroups is not needed a second time here.
const resolveEntryColor = (event, employeeList) => {
    // qfupcal-6e3b9d47 — FIRST, before any job-shaped branch. A follow-up never
    // takes a person colour; the agenda dot reads this same function, so the dot
    // and the grid block can never disagree.
    if (isQuoteFollowUpEntry(event)) {
        return event.isOverdue ? FOLLOW_UP_COLOR_OVERDUE : FOLLOW_UP_COLOR;
    }
    if (event?.resource?.eventType === 'Time Off') {
        const timeOffType = event.resource?.timeOffType;
        if (timeOffType === 'Vacation') return '#f59e0b'; // amber
        if (timeOffType === 'Sick') return '#64748b';     // slate
        return '#8b5cf6';                                 // purple for Personal / other
    }
    if (event?.resource?.status === 'Canceled') return '#a0aec0';
    if (event?.resource?.status === 'Completed') return '#68a37a';
    if (event?.resource?.status === 'Pending') return '#dc2626';

    const primaryAssigneeId = event?.resource?.assignedTo?.[0];
    const backgroundColor = getColorForEmployee(primaryAssigneeId, employeeList);
    return event?.isCrewAppearance
        ? (event.crewColor || backgroundColor)
        : (event?.isProjected ? '#a9a9a9' : backgroundColor);
};

// calsel-6b2f9d34 — Select all / Clear all on the team filter.
// WHY: every person had to be ticked individually, and the filter seeds to
// "only me" (CrmApp.js useEffect on user.uid), so an admin wanting the whole
// team clicked once per head. Two explicit buttons rather than one toggling
// link (the WeeklyPlannerView precedent): a toggle's label is ambiguous when
// SOME are ticked, which is the normal state here.
// The empty-state line is part of the same seam, not a nicety — the events
// memo below returns NO events when an admin has nothing ticked, so Clear all
// puts a blank calendar one click away and it has to explain itself.
const EmployeeFilter = ({ employees, selectedEmployees, onEmployeeFilterChange, onEmployeeFilterSetAll }) => {
    if (!employees || employees.length === 0) return null;

    const allSelected = employees.every(e => selectedEmployees.includes(e.id));
    const noneSelected = selectedEmployees.length === 0;

    return (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h3 className="font-semibold text-gray-700">Team Members</h3>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => onEmployeeFilterSetAll?.(employees.map(e => e.id))}
                        disabled={allSelected}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-default"
                    >
                        Select all
                    </button>
                    <span className="text-gray-300" aria-hidden="true">|</span>
                    <button
                        type="button"
                        onClick={() => onEmployeeFilterSetAll?.([])}
                        disabled={noneSelected}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-default"
                    >
                        Clear all
                    </button>
                </div>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
                {employees.map(employee => (
                    <div key={employee.id} className="flex items-center">
                        <input
                            type="checkbox"
                            id={`employee-${employee.id}`}
                            checked={selectedEmployees.includes(employee.id)}
                            onChange={() => onEmployeeFilterChange(employee.id)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor={`employee-${employee.id}`} className="ml-2 text-sm text-gray-600">
                            {employee.name}
                        </label>
                    </div>
                ))}
            </div>
            {noneSelected && (
                <p className="mt-3 text-sm text-gray-500 italic">
                    No team members selected, so the calendar is empty. Tick someone above, or choose Select all.
                </p>
            )}
        </div>
    );
};

const CalendarView = ({ user, currentUserProfile, onAddJob, onSelectJob, onUpdateJob, onJobMoved, onCreateRecurringException, onRecordRecurringException, employees, selectedEmployees, onEmployeeFilterChange, onEmployeeFilterSetAll, companySettings }) => {
    const [jobs, setJobs] = useState([]);
    const [recurringJobs, setRecurringJobs] = useState([]);
    // A-CAL: route groups, needed to map a multi-crew job's crews[] to member IDs
    // so the filter can show the job to crew members assigned via line-item crews.
    const [routeGroups, setRouteGroups] = useState([]);
    // qfupcal-6e3b9d47 — SEAM 7B. The org's quotes, for follow-up entries only.
    // Nothing here is ever written; see the listener below.
    const [quotes, setQuotes] = useState([]);
    // s5portal-8a3d6f21 — quoteId → snooze reason, from the staff-only quoteSnoozes collection.
    const [snoozeReasons, setSnoozeReasons] = useState({});
    const navigate = useNavigate();
    const location = useLocation();

    // r3-cal-dateurl-6d1a9f27 — the viewed month is URL state (?date=YYYY-MM-DD), so it
    // survives the editor round-trip (CalendarView unmounts when activeView flips to
    // 'job-editor' and remounts on save — local useState would reset to today), plus a
    // refresh and the back button. Parse LOCAL-midnight, never new Date('YYYY-MM-DD')
    // (that's UTC and renders as the prior day west of Greenwich).
    const parseDateParam = (loc) => {
        const raw = new URLSearchParams(loc.search).get('date');
        if (!raw) return new Date();
        const parts = raw.split('-');
        if (parts.length !== 3) return new Date();
        const [y, m, d] = parts.map(n => parseInt(n, 10));
        if ([y, m, d].some(n => Number.isNaN(n))) return new Date();
        return new Date(y, m - 1, d); // local midnight
    };

    // calreturn-7b2e9d41 — the VIEW is URL state too (?view=), for exactly the reason
    // ?date= is above: this component unmounts when the job editor takes over, so plain
    // useState sent the founder back to Month on every save.
    //
    // The split that makes phones safe: `view` is what is SHOWN, `chosenViewRef` is what
    // the USER picked. Only the latter is ever written to the URL. So being forced to
    // agenda under 768px cannot overwrite a desktop choice — open the same link on a
    // phone and you see agenda, then get your Day view back on the laptop.
    const chosenViewRef = useRef(parseViewParam(location)); // null until they pick one
    const [view, setView] = useState(() => (
        isNarrowScreen() ? 'agenda' : (chosenViewRef.current || 'month')
    ));
    const [date, setDate] = useState(() => parseDateParam(location));

    // Mirrors `date` so the single URL writer below can always read the current value
    // synchronously. See writeCalendarUrl for why a ref and not the state.
    const dateRef = useRef(date);
    const [showCompleted, setShowCompleted] = useState(true);
    // qfupcal-6e3b9d47 — founder decision D7b-3: ON by default. A reminder that
    // is off by default is the silo this seam exists to close.
    const [showFollowUps, setShowFollowUps] = useState(true);
    const [pendingMove, setPendingMove] = useState(null); // { event, start, end } awaiting scope confirmation
    // calres3b-6e4a2f81 — a cross-column drop awaiting its reassign confirmation.
    // { event, start, end, title, lines[], options[{label, sub, removeUid, addUid}], thisVisitOnly } — calclar-5e2a8d14
    const [pendingReassign, setPendingReassign] = useState(null);
    const [dropNotice, setDropNotice] = useState(null); // calclar-5e2a8d14 — { text, show:[{uid,name}] } after a reassign drop
    useEffect(() => {
        if (!dropNotice) return undefined;
        const t = setTimeout(() => setDropNotice(null), 15000); // long enough to press "Show <name>"
        return () => clearTimeout(t);
    }, [dropNotice]);
    // calpol-9c4e7b23 — calendar search. Deliberately NOT URL state, unlike `date`:
    // a search is a momentary act, not a place you want to come back to, and the
    // results panel closing on navigation is the wanted behaviour.
    const [searchTerm, setSearchTerm] = useState('');
    const searchBoxRef = useRef(null);

    // calreturn-7b2e9d41 — act only when the 768px boundary is actually CROSSED.
    //
    // The previous version ran its full body on EVERY resize event, and its else-branch
    // fired setView('month'). That meant any desktop window resize silently threw away
    // the founder's Day or Week view — a live bug underneath the one this seam fixes,
    // and the reason persisting the view would otherwise have been a lie.
    //
    // Forcing agenda deliberately does NOT touch chosenViewRef, so the URL keeps what he
    // picked and widening the window restores it.
    useEffect(() => {
        let wasNarrow = isNarrowScreen();
        const handleResize = () => {
            const nowNarrow = isNarrowScreen();
            if (nowNarrow === wasNarrow) return;
            wasNarrow = nowNarrow;
            setView(nowNarrow ? 'agenda' : (chosenViewRef.current || 'month'));
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // calpol-9c4e7b23 — close the search results on a click outside the box.
    // Escape and picking a result already close it; without this, clicking away
    // leaves a panel floating over the calendar you then have to dismiss on purpose.
    // Bound only while there is something to close, so the calendar carries no
    // document listener in its normal state.
    useEffect(() => {
        if (!searchTerm) return undefined;
        const onDocMouseDown = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [searchTerm]);

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        
        const orgId = currentUserProfile.organizationId;
        const jobsPath = `organizations/${orgId}/jobs`;

        const qJobs = query(collection(db, jobsPath));

        const unsubscribeJobs = onSnapshot(qJobs, (snapshot) => {
            const jobsData = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id, // s6costing-docid: doc id wins over a stale stored `id` field (6 test-org occurrences carry their template's id)
                startAt: doc.data().startAt?.toDate(),
                endAt: doc.data().endAt?.toDate(),
            }));
            setJobs(jobsData);
        }, (error) => {
            console.error("!!! ERROR fetching JOBS:", error);
        });
        
        return () => unsubscribeJobs();

    }, [currentUserProfile]);

    // A-CAL: subscribe to route groups so multi-crew jobs (whose crews live in
    // crews[] / line items) can be matched to the employees filtering the calendar.
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;
        const routeGroupsPath = `organizations/${orgId}/routeGroups`;
        const qRouteGroups = query(collection(db, routeGroupsPath));
        const unsubscribeRouteGroups = onSnapshot(qRouteGroups, (snapshot) => {
            setRouteGroups(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => {
            console.error("!!! ERROR fetching ROUTE GROUPS:", error);
        });
        return () => unsubscribeRouteGroups();
    }, [currentUserProfile]);

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        const recurringJobsPath = `organizations/${orgId}/recurringJobs`;
        const qRecurring = query(collection(db, recurringJobsPath));
        const unsubscribeRecurring = onSnapshot(qRecurring, (snapshot) => {
            const recurringData = snapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id, // s6costing-docid: doc id wins over a stale stored `id` field
                startAt: doc.data().startAt?.toDate(),
                endAt: doc.data().endAt?.toDate(),
                recurrence: {
                    ...doc.data().recurrence,
                    endDate: doc.data().recurrence?.endDate?.toDate(),
                }
            }));
            setRecurringJobs(recurringData);
        }, (error) => {
            console.error("!!! ERROR fetching RECURRING JOBS:", error);
        });

        return () => unsubscribeRecurring();
    }, [currentUserProfile]);

    // --- Logic to Include Owners ---
    const canViewTeam = useMemo(() => {
        const role = currentUserProfile?.role;
        return ['Admin', 'Manager', 'Owner', 'Owner/System Admin', 'System Admin'].includes(role);
    }, [currentUserProfile?.role]);

    // qfupcal-6e3b9d47 — SEAM 7B, founder decision D7b-2. Who sees a follow-up.
    //
    // DISPLAY FILTERING ONLY — no permission is changed and no rule is touched.
    // firestore.rules already lets EVERY org member read EVERY quote (the quotes
    // read gate is role-blind), so this is about what is useful to show, not
    // about what is reachable.
    //
    // VIEW_FINANCIALS is the same gate the Quotes page itself uses, so anyone who
    // cannot open an estimate never sees a follow-up for one. Field Technicians
    // hold no VIEW_FINANCIALS, so for them this seam is inert end to end: no
    // listener, no entries, no toggle.
    const canSeeFollowUps = useMemo(
        () => can(currentUserProfile, PERMISSIONS.VIEW_FINANCIALS),
        [currentUserProfile]
    );

    // Managers/Owners/Admins see everyone's; everyone else who can see estimates
    // sees only their own. Deliberately keyed on canViewTeam, the same predicate
    // that decides whether the team filter exists at all, so "can see the team"
    // means one thing in this file rather than two.
    const canSeeOthersFollowUps = canViewTeam;

    // qfupcal-6e3b9d47 — the quotes listener. Listener #4 in this file.
    //
    // NO `where` CLAUSE, ON PURPOSE. There is no firestore.indexes.json anywhere
    // in this repo (firebase.json declares only rules), so any composite index
    // would have to be hand-made from a console error link and would live in no
    // version-controlled file. An unfiltered query needs none, matches the shape
    // of the three listeners above, and is exactly what QuotesView already does
    // on this same collection. Volume: the biggest org holds 95 quotes against
    // the 271 jobs this view already loads unclamped. Filtering happens in the
    // events memo via quoteHasOpenFollowUp.
    useEffect(() => {
        if (!currentUserProfile?.organizationId || !canSeeFollowUps) {
            setQuotes([]);
            return undefined;
        }
        const orgId = currentUserProfile.organizationId;
        const quotesPath = `organizations/${orgId}/quotes`;
        const qQuotes = query(collection(db, quotesPath));
        const unsubscribeQuotes = onSnapshot(qQuotes, (snapshot) => {
            // estdel-f9ac8ba8 — a deleted estimate has no follow-up or snooze on the calendar
            setQuotes(snapshot.docs.filter(doc => !isQuoteArchived(doc.data())).map(doc => ({
                id: doc.id,
                ...doc.data(),
                nextFollowUpAt: doc.data().nextFollowUpAt?.toDate(),
                snoozedUntil: doc.data().snoozedUntil?.toDate(),
            })));
        }, (error) => {
            console.error("!!! ERROR fetching QUOTES:", error);
        });
        // s5portal-8a3d6f21 — snooze reasons moved off the quote (the customer portal reads whole
        // quote docs) into organizations/{orgId}/quoteSnoozes, which is staff-read only. One small
        // listener, same gate as the quotes listener above; only the wording comes from here.
        const unsubscribeSnoozes = onSnapshot(collection(db, `organizations/${orgId}/quoteSnoozes`), (snapshot) => {
            const next = {};
            snapshot.docs.forEach((d) => { if (d.data().reason) next[d.id] = d.data().reason; });
            setSnoozeReasons(next);
        }, (error) => {
            console.warn('CalendarView: snooze reasons unavailable', error);
            setSnoozeReasons({});
        });
        return () => { unsubscribeQuotes(); unsubscribeSnoozes(); };
    }, [currentUserProfile, canSeeFollowUps]);

    // calres-7f2b9d41 — the day view's columns: ticked team members in the TEAM
    // FILTER'S OWN ORDER (we filter `employees` rather than mapping
    // `selectedEmployees`, so the columns read left-to-right in the same order the
    // checkboxes read, not in the order they happened to be ticked), then
    // "Unassigned" last.
    //
    // The Unassigned column is appended UNCONDITIONALLY. It cannot go missing even
    // when nobody is ticked, which is the whole point — see UNASSIGNED_RESOURCE_ID.
    //
    // Null for a non-manager: a technician has no team filter and no ticks, so a
    // single column bearing their own name would be noise. They keep today's plain
    // day view.
    const dayResources = useMemo(() => {
        if (!canViewTeam) return null;
        const ticked = (employees || [])
            .filter(e => selectedEmployees.includes(e.id))
            .map(e => ({ id: e.id, title: e.name || 'Unnamed' }));
        return [...ticked, { id: UNASSIGNED_RESOURCE_ID, title: 'Unassigned' }];
    }, [canViewTeam, employees, selectedEmployees]);

    // The person-columns actually on screen. Derived from dayResources rather than
    // from selectedEmployees so the "every resourceId exists in resources"
    // invariant holds by construction: a ticked id with no matching employee row
    // gets no column, and a job of theirs lands in Unassigned rather than vanishing.
    const resourceIdSet = useMemo(
        () => new Set((dayResources || []).map(r => r.id).filter(id => id !== UNASSIGNED_RESOURCE_ID)),
        [dayResources]
    );

    // calpol-9c4e7b23 — ONE definition of "may this job appear on this person's
    // calendar", extracted from the events memo below so the new search box can ask
    // the same question instead of re-deriving it. The body is byte-identical to
    // what was inlined here before; the only addition is `ignoreTeamFilter`.
    //
    // 🛑 THE TWO THINGS THIS PREDICATE CONFLATES, AND WHY SEARCH SPLITS THEM.
    // It answers two different questions at once:
    //   ROLE visibility — a technician sees only their own jobs (canViewTeam is
    //     false, so employeesToFilterBy is [their uid]). That is not negotiable and
    //     search keeps it.
    //   The TEAM FILTER — a manager's own tick-boxes, a viewing lens they set for
    //     themselves. Search ignores it (founder decision D4-3): a search that
    //     silently hides the job whose number you just typed reads as a bug, not as
    //     a filter working correctly.
    // `ignoreTeamFilter` is therefore honoured ONLY when canViewTeam is already
    // true — it can widen a manager's view to their whole org, and can never widen
    // a technician's beyond their own jobs.
    const jobVisibleTo = useCallback((job, employeesToFilterBy, ignoreTeamFilter = false) => {
        if (ignoreTeamFilter && canViewTeam) return true;
        if (employeesToFilterBy.length === 0 && canViewTeam) {
            return false;
        }
        // Pending jobs with no assignment: always show to admins/managers so
        // they don't accidentally double-book over a slot awaiting confirmation.
        if (canViewTeam && (!job.assignedTo || job.assignedTo.length === 0) && !job.routeGroupId) return true;
        // Check direct assignedTo (individual assignments)
        const directMatch = job.assignedTo?.some(id => employeesToFilterBy.includes(id));
        if (directMatch) return true;
        // Also check assignedGroupMemberIds — jobs created by the Weekly Planner
        // use group assignment and populate this field instead of (or in addition to) assignedTo
        const groupMatch = job.assignedGroupMemberIds?.some(id => employeesToFilterBy.includes(id));
        if (groupMatch) return true;
        // Fallback: admins/managers see all group-assigned jobs even if no member matches
        // the current filter (e.g. route group member not in the employee list yet)
        if (canViewTeam && job.assignmentMode === 'group' && job.routeGroupId) return true;
        // A-CAL: multi-crew jobs assign crews at the LINE-ITEM level (crews[]) and may
        // have NO top-level routeGroupId, so the checks above can miss them. Show the
        // job if any of its crews is a route group containing a filtered employee.
        // Gates on the actual crews[] data, not on assignedGroupMemberIds being populated.
        if (Array.isArray(job.crews) && job.crews.length > 0) {
            const jobCrewGroupIds = job.crews.map(c => c.crewGroupId).filter(Boolean);
            const crewMemberMatch = jobCrewGroupIds.some(gid => {
                const group = routeGroups.find(g => g.id === gid);
                return group?.memberIds?.some(mid => employeesToFilterBy.includes(mid));
            });
            if (crewMemberMatch) return true;
        }
        return false;
    }, [canViewTeam, routeGroups]);

    const events = useMemo(() => {
        const employeesToFilterBy = canViewTeam ? selectedEmployees : (user ? [user.uid] : []);

        const filteredJobs = jobs.filter(job => jobVisibleTo(job, employeesToFilterBy));

        const validJobs = filteredJobs.filter(job => {
            if (!job.startAt || !job.endAt) return false;
            if (!showCompleted && (job.status === 'Completed' || job.status === 'Canceled')) return false;
            return true;
        });

        // r3-cs-calfan-4e7b1a90 — fan continuous per-crew jobs into one event PER
        // APPEARANCE (per crew, per day) instead of a single envelope bar. A job with
        // a crewSchedule suppresses its envelope event and emits its appearances; every
        // other job (normal, recurring instance, time-off) is byte-unchanged.
        // Appearance events ARE draggable/resizable: the accessor guards below permit
        // isCrewAppearance and handleJobMove's isCrewAppearance branch owns the write
        // path (which, per SEAM 1b, also records a template exception when the drag
        // moves a recurring instance's envelope off its original day).
        const realEvents = validJobs.flatMap(job => {
            const isCompleted = job.status === 'Completed';
            const isCanceled = job.status === 'Canceled';
            const prefix = isCompleted ? '✓ ' : isCanceled ? '✕ ' : '';

            // jobnumdisp-6a1e9c34 — the job number leads every calendar entry, so it is
            // the part that SURVIVES the day column's ellipsis (css:386-390) rather than
            // the part that gets cut. '' when unnumbered (see formatJobNumber), which
            // restores today's label byte-for-byte.
            //
            // The status glyph stays ahead of it — '✓ #1860 · Morning · …' — because the
            // glyph is a one-character state marker scanned before anything else.
            // That means `prefix` can no longer live INSIDE the label strings below, or
            // it would land after the number and the window; it is applied once at the
            // final assembly instead. Time Off is the one exception and is handled at
            // its own call site: it has never carried a glyph, a window label, or (now)
            // a number — a vacation block is not a job to be looked up.
            const numLabel = formatJobNumber(job.jobNumber);
            const numSeg = numLabel ? `${numLabel} · ` : '';

            if (hasCrewSchedule(job)) {
                return flattenAppearances(job.crewSchedule).map(appt => {
                    const s = appt.startAt?.toDate ? appt.startAt.toDate() : (appt.startAt instanceof Date ? appt.startAt : null);
                    const e = appt.endAt?.toDate ? appt.endAt.toDate() : (appt.endAt instanceof Date ? appt.endAt : null);
                    if (!s || !e) return null; // skip an un-coercible appearance defensively
                    const crewName =
                        routeGroups.find(g => g.id === appt.crewGroupId)?.name
                        || (Array.isArray(job.crews) ? job.crews.find(c => c.crewGroupId === appt.crewGroupId)?.name : null)
                        || 'Crew';
                    // r3-cs-calfan-4e7b1a90 — view-aware label: month view truncates hard,
                    // so lead with the CUSTOMER (the scan target) and trail the crew, which
                    // the event color already encodes. Week/day/agenda have room, so keep the
                    // fuller crew-first label there.
                    // jobnumdisp-6a1e9c34 — `prefix` moved OUT of these strings to the
                    // assembly below so the glyph stays ahead of the job number.
                    const apptTitle = view === 'month'
                        ? `${job.clientName} · ${crewName}`
                        : `${crewName}: ${job.title} - ${job.clientName}`;
                    return {
                        id: `${job.id}__${appt.crewGroupId}__${appt.id}`,
                        title: `${prefix}${numSeg}${apptTitle}`,
                        start: s,
                        end: e,
                        resource: job,
                        isProjected: false,
                        isCrewAppearance: true,
                        crewGroupId: appt.crewGroupId,
                        appearanceId: appt.id,
                        realJobId: job.id,
                        crewColor: getColorForCrew(appt.crewGroupId, routeGroups),
                        // calwin-8d4f1b52 — an appearance is ALWAYS timed, never in the lane.
                        // This is a hard-coded false, not isUntimedJob(job), on purpose: a stored
                        // appearance carries schedulingMode 'window' with arrivalWindow null
                        // (crewSchedule.js:120, PerCrewScheduler.js:141-142, 172-173) while its
                        // times come from real time-text inputs — production appearances are
                        // 08:00→17:00 blocks. Deriving the flag from the mode would push those
                        // nine-hour blocks into the lane and throw their times away. Today's two
                        // crew jobs happen to carry a top-level mode of 'exact' so a derived flag
                        // would not have fired yet; that is luck, not a guarantee, because
                        // JobFormModal.js:2357 normalises a missing appearance mode to 'window'.
                        isUntimed: false,
                        untimedRank: TIMED_RANK,
                        // calres-7f2b9d41 — an appearance belongs to ONE crew, so it shows in
                        // that crew's members' columns only, not in every crew on the job.
                        // Falls back to Unassigned when none of that crew is ticked.
                        resourceId: toResourceId(
                            new Set((routeGroups.find(g => g.id === appt.crewGroupId)?.memberIds || []).filter(Boolean)),
                            resourceIdSet
                        ),
                    };
                }).filter(Boolean);
            }

            // r3-cs-calfan-4e7b1a90 — view-aware normal-job label, mirroring the
            // appearance labels: month view leads with the CUSTOMER (survives the
            // tight-cell truncation) and trails the job title; week/day keep the
            // fuller title-first label. Time Off is untouched (a vacation block
            // shouldn't trail a customer name).
            // jobnumdisp-6a1e9c34 — `prefix` moved OUT of these strings to the assembly
            // below so the glyph stays ahead of the job number.
            // calpol-9c4e7b23 — month now reads `glyph · when · # · customer`
            // (founder decision D4-1). The job TITLE leaves the month string, and the
            // event-type tag moves to the END so it is the first thing the cell's
            // ellipsis eats — Follow-Up already carries its own dashed-white border
            // (eventStyleGetter below), so the tag is redundant there anyway.
            // Neither is lost: `tooltip` further down carries the full label to hover,
            // and week / day / agenda keep the title-first string unchanged.
            const isTimeOff = job.eventType === 'Time Off';
            const typeTag = job.eventType ? ` (${job.eventType})` : '';
            const baseTitle = isTimeOff
                ? job.title
                : view === 'month'
                    ? `${job.clientName}${typeTag}`
                    : `${job.eventType ? `(${job.eventType}) ` : ''}${job.title} - ${job.clientName}`;

            // calwin-8d4f1b52 — a lane entry LEADS with its window label, because the
            // lane no longer shows a position in time and the window is the only thing
            // that says when the customer was told to expect someone. Uses the shared
            // getJobArrivalWindowShortLabel, which reads the job's arrivalWindowSnapshot
            // first (so a historical job keeps the label its org had when it was saved)
            // and live-resolves the key otherwise — the same call shape RoutesView.js:564
            // and JobManagementView.js:248 already use, deliberately without
            // companySettings so no new prop is threaded into this view.
            // Time Off is left alone: a vacation block has no arrival window.
            const untimed = isUntimedJob(job);
            const windowLabel = isTimeOff ? '' : getJobArrivalWindowShortLabel(job);
            // calpol-9c4e7b23 — the window prefix is DROPPED IN AGENDA. Seam 2 gave
            // agenda a Time column that already shows the window (AgendaTimeLabel), so
            // leaving the prefix on printed it twice on every windowed job — and ~95%
            // of production jobs are windowed. The Time column is the one that stays:
            // it is the column agenda exists to show, and it is what a phone reads
            // first. `view` is already a dependency of this memo.
            const withWindow = (untimed && windowLabel && view !== 'agenda')
                ? `${windowLabel} · ${baseTitle}`
                : baseTitle;
            // jobnumdisp-6a1e9c34 — final assembly: glyph, then number, then window, then
            // the label. Time Off keeps NONE of the three leading parts, exactly as before
            // this change: it never carried the glyph (it was absent from its branch above)
            // and never carried a window label, and a vacation block has no job number worth
            // showing even though it does live in the jobs collection and so is assigned one.
            //
            // calpol-9c4e7b23 — month assembles differently: glyph, WHEN, number,
            // customer. Time Off is excluded from the month form as well, so the
            // multi-day vacation bar's content is byte-for-byte what it was before
            // this seam — the bar is the one thing this seam must not touch.
            const normalTitle = isTimeOff
                ? withWindow
                : view === 'month'
                    ? `${prefix}${entryWhenLabel(job.startAt, untimed, windowLabel)} · ${numSeg}${baseTitle}`
                    : `${prefix}${numSeg}${withWindow}`;
            return [{
                id: job.id,
                title: normalTitle,
                // calpol-9c4e7b23 — month drops the job title from the visible string, so
                // hover has to carry it or it is simply gone. Set on MONTH events only;
                // every other view leaves this undefined and the tooltipAccessor falls
                // through to `title`, exactly as it did before this seam.
                tooltip: view === 'month' && !isTimeOff
                    ? `${prefix}${entryWhenLabel(job.startAt, untimed, windowLabel)} · ${numSeg}${job.clientName} · ${job.title}${typeTag}`
                    : undefined,
                start: job.startAt,
                end: job.endAt,
                resource: job,
                isProjected: false,
                isUntimed: untimed,
                untimedRank: untimed ? untimedRankForJob(job) : TIMED_RANK,
                // calres-7f2b9d41 — Time Off reaches its person through assignedTo, which is
                // what EmployeeProfileModal.js:469 writes (`assignedTo: [targetUid]`), so it
                // lands in that person's column and nobody else's. ⚠️ The OTHER time-off
                // writer, TeamMemberModal.js:623-625, writes `assignedTo: []` plus
                // `assignedFieldWorkerIds: [<teamMembers record id>]` — a different id space
                // with no users uid, so it has no column to go to and lands in Unassigned.
                // That is the same place it already appears today (the events filter treats an
                // empty assignedTo as unassigned), so this is not a regression; see BACKLOG.
                resourceId: toResourceId(resolveJobMemberIds(job, routeGroups), resourceIdSet),
            }];
        });

        const projectedEvents = [];
        // r1-rec-cal-route-b8d3f5a1
        // Recurrence projection runs through the shared engine (utils/recurrence.js)
        // so the Calendar and the Jobs list/Kanban expand recurring occurrences via
        // ONE code path. The engine now owns dedup: we hand it the raw `jobs` array
        // as realInstances and it builds the series-scoped, day-level dedup key
        // itself (recurringJobId_yyyy-MM-dd, viewer's zone, Canceled instances kept).
        // Non-recurring realEvents are intentionally no longer part of the
        // suppression set — a one-off event that merely shares a slot must not
        // suppress a recurring projection (see plan §2 note).
        projectRecurringOccurrences({
            templates: recurringJobs,
            realInstances: jobs,
            windowStart: null,
            windowEnd: null,
            tz: companySettings?.timezone, // orgtime-7c2e4b19 — a series runs through its end date's company day (dep listed below)
            visitWallClockEnabled: companySettings?.visitWallClockEnabled, // tzhour-6a3d9c58 — projections at the times the server will create (dep listed below)
        }).forEach(({ template, jobStart, jobEnd }) => {
            // calwin-8d4f1b52 — a projection inherits the template's scheduling shape, so a
            // recurring window job projects untimed occurrences. Correct by construction:
            // the recurring propagation allowlist already carries schedulingMode /
            // arrivalWindow / arrivalWindowSnapshot onto materialised instances
            // (functions/index.js:3339), so a projection and the real instance it becomes
            // land in the same lane rather than jumping between lane and grid.
            const untimed = isUntimedJob(template);
            const windowLabel = template.eventType === 'Time Off' ? '' : getJobArrivalWindowShortLabel(template);
            const baseTitle = `${template.title} - ${template.clientName}`;
            // calpol-9c4e7b23 — a projection gets the same treatment as a real job, or
            // a recurring series would be the one thing on the month grid with no time
            // on it. A projection has no jobNumber (it has no document yet), so the
            // number segment is simply absent rather than faked.
            const projMonthTitle = `${entryWhenLabel(jobStart, untimed, windowLabel)} · ${template.clientName}`;
            projectedEvents.push({
                id: `${template.id}_${jobStart.toISOString()}`,
                title: view === 'month'
                    ? projMonthTitle
                    : (untimed && windowLabel && view !== 'agenda')
                        ? `${windowLabel} · ${baseTitle}`
                        : baseTitle,
                tooltip: view === 'month'
                    ? `${entryWhenLabel(jobStart, untimed, windowLabel)} · ${template.clientName} · ${template.title}`
                    : undefined,
                start: jobStart,
                end: jobEnd,
                resource: { ...template, startAt: jobStart, endAt: jobEnd },
                isProjected: true,
                isUntimed: untimed,
                untimedRank: untimed ? untimedRankForJob(template) : TIMED_RANK,
                // calres-7f2b9d41 — a projection inherits the template's assignment, so it
                // appears in the same column(s) the real instance will once materialised.
                resourceId: toResourceId(resolveJobMemberIds(template, routeGroups), resourceIdSet),
            });
        });

        // ─── qfupcal-6e3b9d47 — SEAM 7B: quote follow-ups ───────────────────
        //
        // The fourth event shape. Drawn straight from the quote (A4.1): no job
        // document exists for any of these, and nothing below writes anything.
        //
        // resource is NULL and entryKind is the discriminator — see the block
        // comment at the top of this file for why that is the safe shape rather
        // than handing a quote to code that expects a job.
        const followUpEvents = [];
        if (showFollowUps && canSeeFollowUps) {
            const now = new Date();
            quotes.forEach(quote => {
                if (!quoteHasOpenFollowUp(quote)) return;
                const nextAt = quote.nextFollowUpAt;
                if (!(nextAt instanceof Date) || Number.isNaN(nextAt.getTime())) return;

                // D7b-2: managers see everyone's; everyone else sees only their own.
                const salesPersonId = resolveQuoteSalesPerson(quote);
                if (!canSeeOthersFollowUps && salesPersonId !== user?.uid) return;

                // followUpHasTime — NOT nextFollowUpHasTime, which is only the
                // callable's argument name and the activity-entry key. A date-only
                // follow-up is stored as noon local (LogFollowUpModal), so reading
                // the wrong field would render every one of them as "12:00 PM" —
                // a promise nobody made. Date-only goes to the Arrival window lane.
                const untimed = quote.followUpHasTime !== true;
                const snoozedUntil = quote.snoozedUntil instanceof Date ? quote.snoozedUntil : null;
                const snoozed = !!(snoozedUntil && snoozedUntil > now);
                const overdue = isFollowUpOverdue(nextAt, snoozedUntil, now, companySettings?.timezone);

                // A snoozed quote holds the SAME instant in nextFollowUpAt and
                // snoozedUntil, so the entry already sits on the wake-up date;
                // only the reason has to be said out loud.
                const reasonSuffix = snoozed && snoozeReasons[quote.id] ? ` — ${snoozeReasons[quote.id]}` : ''; // s5portal-8a3d6f21
                const customerName = quote.customerDetails?.name || quote.customerName || 'Unknown customer';
                // The E-/Q- prefix is already baked into quoteNumber by the server,
                // so the entry never needs the word "Estimate" — which is why this
                // surface needs no terminology resolution at all.
                const numberLabel = quote.quoteNumber || '';
                const title = `☎ Follow up${numberLabel ? ` · ${numberLabel}` : ''} · ${customerName}${reasonSuffix}`;

                followUpEvents.push({
                    id: `followup_${quote.id}`,
                    entryKind: ENTRY_KIND_FOLLOW_UP,
                    quoteId: quote.id,
                    title,
                    tooltip: `Follow up${numberLabel ? ` · ${numberLabel}` : ''} · ${customerName}${reasonSuffix}`,
                    start: nextAt,
                    // A follow-up is an INSTANT, not a span — but the time grid sizes
                    // a block from its duration, so end === start renders a timed one
                    // as a zero-height sliver you cannot see or click. 30 minutes is a
                    // rendering minimum only: nothing is stored, and a date-only entry
                    // never reaches the grid at all, so it keeps the bare instant.
                    end: untimed ? nextAt : new Date(nextAt.getTime() + 30 * 60 * 1000),
                    resource: null,        // load-bearing — see the top-of-file comment
                    isProjected: false,
                    isOverdue: overdue,
                    isSnoozed: snoozed,
                    isUntimed: untimed,
                    untimedRank: untimed ? UNTIMED_RANK_FOLLOW_UP : TIMED_RANK,
                    // The salesperson's column when they are ticked, Unassigned
                    // otherwise. toResourceId never returns [], which is what stops
                    // an entry vanishing silently — see UNASSIGNED_RESOURCE_ID.
                    resourceId: toResourceId(
                        new Set(salesPersonId ? [salesPersonId] : []),
                        resourceIdSet
                    ),
                });
            });
        }

        // calwin-8d4f1b52 — we now own ordering outright, so sort once here.
        return [...realEvents, ...projectedEvents, ...followUpEvents].sort(compareCalendarEvents);
    }, [jobs, recurringJobs, canViewTeam, selectedEmployees, user, showCompleted, routeGroups, view, resourceIdSet, jobVisibleTo,
        quotes, snoozeReasons, showFollowUps, canSeeFollowUps, canSeeOthersFollowUps, // snoozeReasons: s5portal-8a3d6f21
        // qattn-8c4e1f92-cal-tz — a real dependency, not a lint appeasement: the
        // overdue colour is computed against the ORG's day boundary, so an org
        // changing its timezone must repaint the follow-up entries.
        companySettings?.timezone, companySettings?.visitWallClockEnabled]); // visitWallClockEnabled: tzhour-6a3d9c58

    // calpol-9c4e7b23 — the per-day visit count shown in the month date header.
    //
    // Built from the SAME `events` array the cells render, so the number can never
    // disagree with the rows underneath it — including when the team filter or the
    // "showing completed" toggle changes what is on screen. It deliberately counts
    // entries that are hidden behind "+N more": the header answers "how much is on
    // this day", not "how many rows fit".
    //
    // TIME OFF IS EXCLUDED. A vacation is not a visit, and a five-day block would
    // otherwise add 1 to five separate days' counts and make a quiet week look busy.
    //
    // A multi-day JOB is counted on every day it spans, because it genuinely
    // occupies each of those days. The walk is capped at 366 iterations: `endAt` is
    // user-entered and one bad value (a typo'd year) would otherwise spin this loop
    // forever and hang the tab.
    const visitCountByDay = useMemo(() => {
        const counts = new Map();
        const MAX_DAYS = 366;
        events.forEach(evt => {
            if (evt.resource?.eventType === 'Time Off') return;
            // qfupcal-6e3b9d47 — a phone call you owe is not a visit. Same reason
            // the Time Off exclusion above exists: this count answers "how many
            // times are we going out that day".
            if (isQuoteFollowUpEntry(evt)) return;
            if (!(evt.start instanceof Date) || Number.isNaN(evt.start.getTime())) return;
            const last = (evt.end instanceof Date && !Number.isNaN(evt.end.getTime())) ? evt.end : evt.start;
            let cursor = startOfDay(evt.start);
            const lastKey = format(startOfDay(last), 'yyyy-MM-dd');
            for (let i = 0; i < MAX_DAYS; i += 1) {
                const key = format(cursor, 'yyyy-MM-dd');
                counts.set(key, (counts.get(key) || 0) + 1);
                if (key === lastKey) break;
                cursor = add(cursor, { days: 1 });
                // An end BEFORE the start would never reach lastKey; stop at one day.
                if (isBefore(last, cursor)) break;
            }
        });
        return counts;
    }, [events]);

    // calpol-9c4e7b23 — the components object has to move inside the component now
    // that two of its members need data (the day counts, the employee colours).
    //
    // 🛑 IT MUST BE MEMOIZED. Building this inline in the JSX would hand React a new
    // component identity on every render, which remounts EVERY event node on the
    // calendar — visible as flicker and as lost drag state. The deps are the two
    // things the closures actually read.
    //
    // Per-view namespacing is the library's own: Calendar.js:303 merges
    // `components[view]` over the top-level keys, so `month.dateHeader` is
    // unreachable from week/day/agenda and `agenda.event` is unreachable from
    // month — which is exactly why month keeps the library's default event
    // rendering and its multi-day Time Off bar is untouched by any of this.
    const calendarComponents = useMemo(() => ({
        timeGutterHeader: ArrivalWindowGutterLabel,
        month: {
            dateHeader: (props) => (
                <MonthDateHeader {...props} count={visitCountByDay.get(format(props.date, 'yyyy-MM-dd')) || 0} />
            ),
        },
        agenda: {
            time: AgendaTimeLabel,
            event: (props) => (
                <AgendaEventContent {...props} color={resolveEntryColor(props.event, employees)} />
            ),
        },
    }), [visitCountByDay, employees]);

    const handleSelectEvent = (event) => {
        // qfupcal-6e3b9d47 — a follow-up opens the ESTIMATE, never the job editor.
        // /quotes/:id is already a supported deep link (CrmApp.js), so this needs
        // no new prop and never touches CrmApp's job decision tree — which is
        // duplicated in two places and would otherwise have to change in both.
        if (isQuoteFollowUpEntry(event)) {
            // calreturn-7b2e9d41 — carry the exact calendar URL so the estimate can offer
            // "Back to Calendar" and land him on the same day and view he left.
            if (event.quoteId) {
                navigate(`/quotes/${event.quoteId}`, { state: { returnTo: currentCalendarUrl() } });
            }
            return;
        }
        if (event.resource) {
            onSelectJob(event.resource);
        }
    };

    // r3-cal-dateurl-6d1a9f27 + calreturn-7b2e9d41 — ONE writer for the calendar URL.
    //
    // Why one, and why fed from refs: drilling into a day from month view makes rbc fire
    // onNavigate AND onView in the same tick. Two independent handlers each reading
    // `location.search` from the same stale render would race, and whichever navigate()
    // landed second would silently drop the other's param. Refs update synchronously, so
    // this always sees both current values.
    //
    // Date is formatted from LOCAL fields (no toISOString, which shifts the day west of
    // Greenwich). Other params are carried through from window.location — not the
    // `location` hook — because navigate() updates history synchronously, so the DOM is
    // current even mid-tick while the hook's value is not.
    const buildCalendarUrl = (d, chosenView) => {
        const pad = (n) => String(n).padStart(2, '0');
        const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        const params = new URLSearchParams(window.location.search);
        params.set('date', key);
        if (chosenView) params.set('view', chosenView);
        return `/calendar?${params.toString()}`;
    };

    // Writes and returns the URL, so callers that navigate away immediately afterwards
    // can hand it on as their returnTo rather than re-deriving a stale one.
    const writeCalendarUrl = (d, chosenView) => {
        const url = buildCalendarUrl(d, chosenView);
        navigate(url, { replace: true });
        return url;
    };

    // The calendar URL as it stands right now — the returnTo handed to every screen
    // opened from here.
    const currentCalendarUrl = () => buildCalendarUrl(dateRef.current, chosenViewRef.current);

    const handleViewChange = (newView) => {
        setView(newView);
        chosenViewRef.current = newView; // an explicit choice: this one DOES reach the URL
        writeCalendarUrl(dateRef.current, newView);
    };

    const handleNavigate = (newDate) => {
        setDate(newDate);
        dateRef.current = newDate;
        return writeCalendarUrl(newDate, chosenViewRef.current);
    };

    // calpol-9c4e7b23 — CALENDAR SEARCH.
    //
    // A RESULTS LIST, NOT A FILTER, and that is the whole design decision. Filtering
    // what is already drawn can only ever find jobs inside the visible range — and
    // the reason you are searching is usually that you do not know which month the
    // job is in. This view already holds the org's ENTIRE jobs collection in state
    // with no date clamp (the `jobs` subscription above queries the collection
    // unfiltered), so searching across all time costs nothing and needs no new read.
    //
    // WHAT IT SEARCHES, stated rather than left to be discovered:
    //   • REAL job documents only. A projected recurring occurrence has no document,
    //     no job number, and no single date to jump to — its template does. Out of
    //     scope, and logged in BACKLOG.
    //   • Regardless of the "showing completed & cancelled" toggle. That toggle is
    //     about reducing clutter on the grid; you should still be able to find a job
    //     you finished last week.
    //   • Ignoring the team filter but NOT the role — see jobVisibleTo for why.
    const searchResults = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return null; // null means "not searching", distinct from "no matches"
        const employeesToFilterBy = canViewTeam ? selectedEmployees : (user ? [user.uid] : []);
        const matches = jobs
            .filter(job => job.startAt instanceof Date && !Number.isNaN(job.startAt.getTime()))
            .filter(job => jobVisibleTo(job, employeesToFilterBy, true))
            .filter(job => matchesJobSearch(job, term))
            // Soonest first, so today's work is at the top rather than 2023's.
            .sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
        return { matches, shown: matches.slice(0, 20) };
    }, [searchTerm, jobs, canViewTeam, selectedEmployees, user, jobVisibleTo]);

    // Jump the calendar to the job's day and open it. handleNavigate is reused
    // rather than setDate, because it also writes ?date= — which is what makes the
    // day survive the job editor unmounting and remounting this view
    // (r3-cal-dateurl-6d1a9f27). No URL round-trip is involved: the search box lives
    // INSIDE this component, so it calls the state setter directly. That matters,
    // because parseDateParam runs only in the useState lazy initializer and nothing
    // syncs location.search back into state afterwards.
    const handleSearchResultClick = (job) => {
        setSearchTerm('');
        handleNavigate(startOfDay(job.startAt));
        onSelectJob(job);
    };

    // qfupcal-6e3b9d47 — follow-up results, as a SEPARATE memo.
    //
    // WHY THIS EXISTS AT ALL: once follow-ups are on the calendar, a search that
    // finds only jobs is the same defect decision D4-3 was made to avoid — a
    // search that silently hides the thing whose number you just typed reads as
    // a bug. Leaving it out would have created that bug rather than deferred it.
    //
    // Cheap because search reads the raw state array and never `events`, so this
    // adds a memo instead of complicating the one above.
    //
    // It deliberately IGNORES showFollowUps: that toggle is about clutter on the
    // grid, exactly as the completed/cancelled toggle is for the job search — you
    // should still be able to FIND a follow-up you have hidden.
    const followUpSearchResults = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term || !canSeeFollowUps) return null;
        const matches = quotes
            .filter(quoteHasOpenFollowUp)
            .filter(q => q.nextFollowUpAt instanceof Date && !Number.isNaN(q.nextFollowUpAt.getTime()))
            .filter(q => canSeeOthersFollowUps || resolveQuoteSalesPerson(q) === user?.uid)
            .filter(q => matchesQuoteSearch(q, term))
            .sort((a, b) => a.nextFollowUpAt.getTime() - b.nextFollowUpAt.getTime());
        return { matches, shown: matches.slice(0, 10) };
    }, [searchTerm, quotes, canSeeFollowUps, canSeeOthersFollowUps, user]);

    // Same contract as handleSearchResultClick: jump the calendar to the day via
    // handleNavigate (so ?date= is written), then open the estimate.
    const handleFollowUpResultClick = (quote) => {
        setSearchTerm('');
        // calreturn-7b2e9d41 — returnTo is the URL handleNavigate JUST wrote, not one
        // rebuilt from `location`: the jump to the follow-up's day has already happened,
        // so re-deriving it here would send him back to the day he searched FROM.
        const returnTo = handleNavigate(startOfDay(quote.nextFollowUpAt));
        navigate(`/quotes/${quote.id}`, { state: { returnTo } });
    };

    // r3-reschedule-ghost-seam1 — SEAM 1 shared helper. When a REAL materialized
    // recurring instance is dragged to a DIFFERENT calendar day (either as an
    // ordinary event, 1a, or as a per-crew appearance, 1b), record its ORIGINAL
    // slot day as an exception on the template so the vacated day stops
    // projecting a ghost. The day is computed with date-fns format in the
    // viewer's LOCAL zone — the exact basis the projection engine's exception
    // check uses (recurrence.js) — and NEVER the UTC oldDateStr / toISOString
    // basis at the route-sync below, which shifts an evening job to the next day
    // and would then PERMANENTLY hide a real future occurrence (an exception is
    // unrecoverable through any UI, function, or script). Writes ONLY when the
    // local day genuinely changed; a same-day / time-only move writes nothing,
    // and non-recurring jobs (no recurringJobId) write nothing.
    const recordRecurringMoveException = (job, originalDate, newDate) => {
        if (!job?.recurringJobId || !onRecordRecurringException) return;
        const origDate = originalDate?.toDate ? originalDate.toDate() : (originalDate ? new Date(originalDate) : null);
        const nextDate = newDate?.toDate ? newDate.toDate() : (newDate ? new Date(newDate) : null);
        if (!origDate || !nextDate || isNaN(origDate.getTime()) || isNaN(nextDate.getTime())) return;
        const origDateStr = format(origDate, 'yyyy-MM-dd');
        const newDateStr = format(nextDate, 'yyyy-MM-dd');
        if (origDateStr === newDateStr) return; // day unchanged — write no exception
        onRecordRecurringException(job.recurringJobId, origDateStr);
    };

    // calres3b-6e4a2f81 — decide what a cross-column drop means, or why it is refused.
    // Returns { ok:false, reason } or { ok:true, ...pendingReassign fields }.
    const planReassign = (event, targetId, ownColumns) => {
        const job = event.resource;

        // Refusal 1 — crew-assigned (D2). Checked before anything else, and
        // isCrewAppearance is included because a per-crew block belongs to a crew job.
        if (event.isCrewAppearance || isCrewAssigned(job)) {
            return {
                ok: false,
                reason: "This job is assigned through a crew, so dragging can't move it to one person. Open the job and change the crew or the assignment there.",
            };
        }

        const droppingOnUnassigned = targetId === UNASSIGNED_RESOURCE_ID;
        // The people this event is actually shown under, excluding the synthetic column.
        const shownUids = ownColumns.filter(id => id !== UNASSIGNED_RESOURCE_ID);

        // Founder decision D3b-3 — a reassign only ever applies to the dragged visit.
        // Deliberately keyed on the two shapes executeReassign actually DETACHES (a
        // materialised instance, or a projection it materialises), NOT on
        // `recurrence.frequency`, so the sentence in the dialog is exactly what the
        // write does. A job doc carrying its OWN recurrence with no recurringJobId
        // would not be detached — there are 0 such docs in production, and this keeps
        // the dialog from promising "this visit only" if one ever appears.
        const thisVisitOnly = !!job?.recurringJobId || !!event.isProjected;

        // calclar-5e2a8d14 — D3b-1 corrected and redesigned. A drop on Unassigned does NOT remove
        // "the person dragged from" (the library can't say which column a multi-column drag left):
        // it offers "Remove only <name>" for each visible person without clocked time, plus
        // "Remove all (<names>)" when nobody has clocked time. A drop on a person offers "Replace
        // <name> with <target>" for each visible person without clocked time, plus "Add <target>,
        // keep <names>". Every option states its result by name, computed by buildReassignment —
        // the same function the write runs — and anyone hidden by the Team filter is named with
        // "(hidden by your Team filter)". Clocked people are never removable (D3b-2); the dialog
        // says why. Same actions as before, plus "Remove all"; only the wording changed.
        const dialog = buildReassignDialog({
            mode: droppingOnUnassigned ? 'unassign' : 'toPerson',
            job, targetId, shownUids, employees,
            isHidden: (uid) => !resourceIdSet.has(uid),
            formatJobNumber,
        });
        return dialog.ok ? { ...dialog, thisVisitOnly } : dialog;
    };

    // calres3b-6e4a2f81 — the single write. `removeUid` is null for an add-only drop
    // and for "add without removing anyone". Time and assignment always travel in ONE
    // write, so a combined move (different person AND different time) is atomic with
    // no transaction; there is no partial state to design around.
    const executeReassign = (pending, removeUid, addUid = null) => {
        const { event, start, end } = pending;
        const job = event.resource;
        const { rows, assignedTo, assignedToNames } = buildReassignment(job, removeUid, addUid, employees);
        setPendingReassign(null);
        // calclar-5e2a8d14 — say who is on the job now, and offer to show anyone the Team filter hides.
        setDropNotice(afterDropNotice({
            job, rows, employees, isHidden: (uid) => !resourceIdSet.has(uid), formatJobNumber,
        }));
        // laborheal-3b7e9c25 — dropping the last person on "Unassigned" empties the labour on
        // purpose; the marker beside the list tells the server this isn't the old mobile app's wipe.
        const clearedMarker = rows.length === 0 ? { 'jobCosting.laborClearedAt': new Date() } : {};

        if (event.isProjected) {
            // No real doc yet. handleCreateRecurringException materialises the
            // occurrence by spreading the template, so the assignment must override
            // that copy — hence the 4th argument.
            onCreateRecurringException(job, start, end, {
                jobCosting: { ...(job.jobCosting || {}), laborEntries: rows },
                assignedTo,
                assignedToNames,
            });
        } else if (job.recurringJobId) {
            // A real materialised instance, detached to this visit only — the same
            // shape executeMoveWithScope writes for scope 'only_this', plus the
            // assignment keys in the same updateDoc.
            onUpdateJob(job.id, {
                startAt: start,
                endAt: end,
                recurringException: true,
                recurrence: { frequency: 'Never' },
                recurringJobId: job.recurringJobId,
                'jobCosting.laborEntries': rows,
                ...clearedMarker,
                assignedTo,
                assignedToNames,
            });
            // The instance may also have left its original day; the helper no-ops on a
            // same-day move and owns the local-zone day basis. An exception is
            // unrecoverable, so this call stays exactly as SEAM 1a wrote it.
            recordRecurringMoveException(job, job.startAt, start);
        } else {
            onUpdateJob(job.id, {
                startAt: start,
                endAt: end,
                'jobCosting.laborEntries': rows,
                ...clearedMarker,
                assignedTo,
                assignedToNames,
            });
        }

        // Same route side-effect as a plain move.
        const oldDateStr = (job.startAt?.toDate ? job.startAt.toDate() : new Date(job.startAt || start))
            .toISOString().split('T')[0];
        const newDateStr = new Date(start).toISOString().split('T')[0];
        if (onJobMoved) onJobMoved(oldDateStr, newDateStr, job);
    };

    const executeMoveWithScope = (event, start, end, scope) => {
        const job = event.resource;
        const jobId = job.id;
        const isRecurringInstance = !!(job.recurringJobId);
        // jobdup-3e7b1c52 — no repeat setting = a one-off, never a series.
        const isRecurringTemplate = !!job.recurrence?.frequency && job.recurrence.frequency !== 'Never';
        const isProjectedInstance = event.isProjected; // projected = no real Firestore doc

        if ((isRecurringInstance || isRecurringTemplate || isProjectedInstance) && scope === 'only_this') {
            if (isProjectedInstance) {
                // Projected instance has no real Firestore doc — create an exception doc
                onCreateRecurringException(job, start, end);
            } else if (isRecurringInstance) {
                // Real instance doc exists — update it
                onUpdateJob(jobId, {
                    startAt: start,
                    endAt: end,
                    recurringException: true,
                    recurrence: { frequency: 'Never' },
                    recurringJobId: job.recurringJobId,
                });
                // SEAM 1a — the instance just moved off its original day; record
                // that day as a template exception so it stops projecting a ghost
                // (helper no-ops on a same-day move).
                recordRecurringMoveException(job, job.startAt, start);
            } else {
                // Template being dragged — update it as a single instance
                onUpdateJob(jobId, { startAt: start, endAt: end });
            }
        } else if ((isRecurringInstance || isRecurringTemplate || isProjectedInstance) && scope === 'all') {
            const templateId = job.recurringJobId || jobId;
            onUpdateJob(templateId, { startAt: start, endAt: end }, 'recurringJobs');
        } else {
            // Non-recurring
            onUpdateJob(jobId, { startAt: start, endAt: end });
        }
        setPendingMove(null);

        // Route sync
        const oldDateStr = (job.startAt?.toDate ? job.startAt.toDate() : new Date(job.startAt || start))
            .toISOString().split('T')[0];
        const newDateStr = new Date(start).toISOString().split('T')[0];
        if (onJobMoved) onJobMoved(oldDateStr, newDateStr, job);
    };

    const handleJobMove = ({ event, start, end, resourceId }) => {
        if (!can(currentUserProfile, PERMISSIONS.MANAGE_JOBS)) {
            alert("You do not have permission to reschedule jobs.");
            return;
        }

        // calres3b-6e4a2f81 — a drop onto a DIFFERENT person's column is a REASSIGN
        // (seam 3b). Seam 3 refused it whole; it now routes through a confirmation and
        // then a single write carrying both the new time and the new assignment.
        //
        // The source column(s) come from the event's OWN resourceId, never from the
        // library's `sourceResource` — that is dead code (EventWrapper builds an
        // `extendedEvent`, sets the field on that copy, then calls onBeginAction with
        // the ORIGINAL event, addons/dragAndDrop/EventWrapper.js:49-51) and
        // `onDragStart` carries only {event, action, direction}. A move within ANY of
        // the event's own columns therefore stays a pure time change, byte-for-byte as
        // before — the invariant seam 3 was built to protect.
        //
        // resourceId is undefined in week/month/agenda and for anyone who is not
        // Admin/Manager/Owner (no resources are supplied, dayResources returns null),
        // so this whole branch is inert outside the day view.
        if (resourceId != null && event.resourceId != null) {
            const ownColumns = Array.isArray(event.resourceId) ? event.resourceId : [event.resourceId];
            if (!ownColumns.includes(resourceId)) {
                const decision = planReassign(event, resourceId, ownColumns);
                if (!decision.ok) { alert(decision.reason); return; }
                // calclar-5e2a8d14 — the WHOLE plan goes to the dialog. Round 1 copied a fixed
                // list of fields here and silently dropped the new ones, so every option read
                // "Nobody is left" and "Remove everyone" never appeared.
                setPendingReassign({ event, start, end, ...decision });
                return;
            }
        }

        // r3-cs-calfanwrite-2c9f6b31 — continuous per-crew appearance drag/resize.
        // A per-crew appearance event owns ONE dated block for ONE crew. Moving or
        // resizing it rewrites just that appearance inside crewSchedule[crewGroupId]
        // and re-derives the top-level envelope via getJobDateSpan (the SAME deriver
        // every read-site uses, so a dragged envelope can't diverge from an
        // editor-saved one). Never routes through executeMoveWithScope (its route-sync
        // reads the envelope date, wrong for an appearance) and never touches
        // recurrence.js. start/end are taken at face value as the new position
        // (month-view keeps the time + shifts the day; week/day-view sets the slot).
        if (event.isCrewAppearance) {
            const job = event.resource;
            const crewGroupId = event.crewGroupId;
            const oldApptId = event.appearanceId;
            const cs = job.crewSchedule || {};
            const entry = cs[crewGroupId];
            const apps = Array.isArray(entry?.appearances) ? entry.appearances : [];

            // Rebuild the moved appearance from the drop, preserving mode/window.
            const orig = apps.find(a => a.id === oldApptId) || {};
            const newDateKey = toDateKey(start);
            const newId = makeAppearanceId(newDateKey, format(start, 'HHmm'));
            const movedAppt = {
                ...orig,
                id: newId,
                date: newDateKey,
                startAt: start,
                endAt: end,
            };

            // Collision guard (decided: reject). If the new slot collides with a
            // DIFFERENT existing appearance of the SAME crew (same id, i.e. same
            // day+start-time), refuse — the (date,start-time) id is the crew's
            // uniqueness invariant, so a merge would silently drop a block.
            if (newId !== oldApptId && apps.some(a => a.id === newId)) {
                alert("That crew is already scheduled at that day and time.");
                return;
            }

            const newApps = apps.map(a => (a.id === oldApptId ? movedAppt : a));
            const newSchedule = {
                ...cs,
                [crewGroupId]: { ...(entry || {}), appearances: newApps },
            };

            // Re-derive the envelope from the SAME helper every read-site uses.
            const span = getJobDateSpan({ ...job, crewSchedule: newSchedule });

            onUpdateJob(job.id, {
                crewSchedule: newSchedule,
                startAt: span.start,
                endAt: span.end,
            });
            // SEAM 1b — a per-crew appearance block can belong to a recurring
            // instance (its crewSchedule is inherited from the template). If this
            // drag shifted the ENVELOPE start day, the instance vacated its
            // original day and must record it as a template exception. Guard on
            // the envelope start day: job.startAt (pre-move envelope) vs the
            // re-derived span.start (new envelope). The helper no-ops for
            // non-recurring crew jobs and for envelope-day-unchanged nudges.
            recordRecurringMoveException(job, job.startAt, span.start);
            return;
        }

        const job = event.resource;
        // Allow projected recurring events — they'll create an exception on drop
        if (event.isProjected && !(job?.recurringJobId) && job?.recurrence?.frequency === 'Never') return;
        const isRecurring = !!(job.recurringJobId) || (!!job.recurrence?.frequency && job.recurrence.frequency !== 'Never') || event.isProjected; // jobdup-3e7b1c52

        if (isRecurring) {
            // Show scope selector before applying the move
            setPendingMove({ event, start, end });
        } else {
            executeMoveWithScope(event, start, end, null);
        }
    };

    const handleJobResize = ({ event, start, end }) => {
        if (!can(currentUserProfile, PERMISSIONS.MANAGE_JOBS)) return;
        if (event.isProjected) return;
        handleJobMove({ event, start, end });
    };

    const handleSelectSlot = ({ start, end, resourceId }) => {
        if (!can(currentUserProfile, PERMISSIONS.MANAGE_JOBS)) {
            return;
        }
        // calclar-5e2a8d14 — a click in a person's day-view column opens the new job already
        // assigned to them (JobFormModal builds their labour row from assignedTo). The
        // Unassigned column and week view (no resourceId) open it unassigned, as before.
        onAddJob(seedForSlot({ start, end, resourceId, employees, unassignedId: UNASSIGNED_RESOURCE_ID }));
    };

    const eventStyleGetter = (event) => {
        const style = {
            borderRadius: '5px',
            opacity: 1,
            color: 'white',
            border: '0px',
            display: 'block'
        };

        // calpol-9c4e7b23 — the background hex is now resolved ONCE, by
        // resolveEntryColor, so the agenda dot can ask for the same answer. The
        // branch structure below is unchanged; each branch simply no longer assigns
        // its own backgroundColor. Every branch's colour, in order, is mirrored in
        // resolveEntryColor — if you add a branch here, add it there too.
        style.backgroundColor = resolveEntryColor(event, employees);

        // qfupcal-6e3b9d47 — a follow-up short-circuits the status chain entirely.
        // It has no status, so it would fall through every branch below anyway;
        // returning here says that on purpose and keeps it away from the
        // eventType 'Follow-Up' JOB branch, whose dashed border means a different
        // thing and must stay telling those two apart.
        //
        // PILL vs the uniform 5px every job carries — a shape difference that reads
        // at a glance in the grid and in month, on a channel nothing else uses.
        if (isQuoteFollowUpEntry(event)) {
            style.borderRadius = '999px';
            style.fontStyle = 'italic';
            if (view === 'agenda') {
                // Agenda flattens background and border below, so italic plus the
                // ☎ glyph in the title are what survive to mark it there.
                style.backgroundColor = 'transparent';
                style.color = '#1f2937';
            }
            return { style };
        }

        if (event.resource?.eventType === 'Time Off') {
            style.opacity = 0.9;
        } else if (event.resource?.status === 'Canceled') {
            style.opacity = 0.6;
            style.textDecoration = 'line-through';
        } else if (event.resource?.status === 'Completed') {
            // Completed: muted green tint, slight fade, no bold colour
            style.opacity = 0.75;
            // calpol-9c4e7b23 — and now struck through, as Canceled already was.
            // Finished work should read as finished; the tint alone was easy to miss
            // beside a Scheduled job of the same person's colour. Applied in EVERY
            // view rather than month only, to match the ✓ glyph, which is everywhere.
            style.textDecoration = 'line-through';
        } else if (event.resource?.status === 'Pending') {
            // Pending: bold red — needs a confirmation call before it becomes Scheduled
        } else if (event.resource?.eventType === 'Follow-Up') {
            style.border = '2px dashed #FFFFFF';
            style.boxSizing = 'border-box';
        }

        // calpol-9c4e7b23 — AGENDA reads as a list, not as coloured stripes.
        // Agenda rows are full-width (the style lands on the <tr>, Agenda.js:57-60),
        // so the saturated person colour that works as a small block in the grid
        // became a solid band across the screen — and a day of them became stripes.
        // The colour is not discarded: AgendaEventContent draws it as a dot at the
        // head of the title, from resolveEntryColor, so it is the same colour the row
        // had. Opacity, strike-through and the Follow-Up border above are all kept,
        // because those encode STATE rather than identity and still need to read.
        if (view === 'agenda') {
            style.backgroundColor = 'transparent';
            style.color = '#1f2937'; // gray-800, legible on the white row
            style.border = '0px';
        }

        return { style };
    };

    // Calculate the length of the agenda based on the current view for better UX
    const agendaLength = useMemo(() => {
        if (view === 'day') return 1;
        if (view === 'week') return 7;
        return 30; // Default fallback for Month/Agenda
    }, [view]);

    return (
        <div className="p-4 sm:p-6 md:p-10 w-full min-w-0">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Calendar</h1>
                
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Route This Day — shown in day view only */}
                    {view === 'day' && can(currentUserProfile, PERMISSIONS.VIEW_ROUTES) && (
                        <button
                            onClick={() => {
                                const dateStr = date.toISOString().split('T')[0];
                                navigate(`/routes?date=${dateStr}`);
                            }}
                            className="flex items-center p-3 bg-green-600 text-white rounded-md font-semibold hover:bg-green-700 whitespace-nowrap"
                        >
                            <MapIcon size={18} className="mr-2" />
                            Route This Day
                        </button>
                    )}
                    {can(currentUserProfile, PERMISSIONS.MANAGE_JOBS) && (
                        <button
                            onClick={() => onAddJob()}
                            className="flex items-center p-3 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 whitespace-nowrap"
                        >
                            <Plus size={20} className="mr-2" />
                            Add Event
                        </button>
                    )}
                </div>
            </div>

            {canViewTeam && (
                <EmployeeFilter
                    employees={employees}
                    selectedEmployees={selectedEmployees}
                    onEmployeeFilterChange={onEmployeeFilterChange}
                    onEmployeeFilterSetAll={onEmployeeFilterSetAll} // calsel-6b2f9d34
                />
            )}
            
            {/* Completed/Cancelled visibility toggle */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <button
                    onClick={() => setShowCompleted(p => !p)}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                        showCompleted
                            ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
                            : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                    }`}
                >
                    <span>{showCompleted ? '✓' : '○'}</span>
                    {showCompleted ? 'Showing completed & cancelled' : 'Hiding completed & cancelled'}
                </button>
                {!showCompleted && (
                    <span className="text-xs text-gray-400 italic">Past completed events are hidden</span>
                )}

                {/* qfupcal-6e3b9d47 — SEAM 7B, founder decision D7b-3: ON by default.
                    Hidden entirely from anyone who cannot see estimates, so a Field
                    Technician is never offered a switch that would do nothing. Styled
                    in the slate the entries themselves use, not the green above — the
                    two toggles control different species of thing. */}
                {canSeeFollowUps && (
                    <button
                        onClick={() => setShowFollowUps(p => !p)}
                        className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                            showFollowUps
                                ? 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
                                : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                        }`}
                    >
                        <PhoneCall size={12} />
                        {showFollowUps ? 'Showing follow-ups' : 'Hiding follow-ups'}
                    </button>
                )}

                {/* calpol-9c4e7b23 — calendar search. Input styling is the compact Jobs
                    convention verbatim (JobManagementView.js:1099-1116) so the two boxes
                    are the same control; undebounced like every other search box in the
                    app, because it filters an in-memory array and triggers no read. */}
                <div className="relative ml-auto w-full sm:w-72" ref={searchBoxRef}>
                    <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') setSearchTerm(''); }}
                        placeholder={canSeeFollowUps ? 'Find a job or follow-up…' : 'Find a job by number, title or customer…'}
                        className="pl-8 pr-8 py-1.5 border rounded-md text-sm text-gray-700 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            aria-label="Clear search"
                        >
                            <XCircle size={14} />
                        </button>
                    )}

                    {/* The results panel floats over the calendar rather than sitting
                        inline, so opening it does not push the grid down the page. */}
                    {searchResults && (
                        <div className="absolute right-0 z-30 mt-1 w-full sm:w-96 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl">
                            {/* qfupcal-6e3b9d47 — the empty state now has to account for BOTH
                                kinds. Saying "No jobs match" while a follow-up result sits
                                underneath it would be the same lie the search box exists to
                                avoid. */}
                            {searchResults.matches.length === 0 && !followUpSearchResults?.matches.length ? (
                                <p className="px-3 py-4 text-sm text-gray-500 text-center">
                                    Nothing matches “{searchTerm.trim()}”.
                                </p>
                            ) : (
                                <>
                                {searchResults.matches.length > 0 && (
                                <>
                                    {/* Said out loud, because it is the one thing about this box
                                        that could otherwise look like a bug: it finds jobs the
                                        calendar behind it is currently hiding. */}
                                    <p className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-gray-400">
                                        {searchResults.matches.length === 1
                                            ? '1 job'
                                            : `${searchResults.matches.length} jobs`}
                                        {canViewTeam ? ' · all team members' : ''}
                                    </p>
                                    {searchResults.shown.map(job => {
                                        const untimed = isUntimedJob(job);
                                        const windowLabel = job.eventType === 'Time Off'
                                            ? ''
                                            : getJobArrivalWindowShortLabel(job);
                                        return (
                                            <button
                                                key={job.id}
                                                type="button"
                                                onClick={() => handleSearchResultClick(job)}
                                                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-t border-gray-100 first:border-t-0"
                                            >
                                                <span className="block text-sm text-gray-800 truncate">
                                                    <span className="font-semibold">{formatJobNumber(job.jobNumber) || '—'}</span>
                                                    {' · '}{job.clientName || job.customerDetails?.name || 'No customer'}
                                                </span>
                                                <span className="block text-xs text-gray-500 truncate">{job.title}</span>
                                                <span className="block text-xs text-gray-400">
                                                    {format(job.startAt, 'EEE d MMM yyyy')}
                                                    {' · '}{entryWhenLabel(job.startAt, untimed, windowLabel)}
                                                    {job.status ? ` · ${job.status}` : ''}
                                                </span>
                                            </button>
                                        );
                                    })}
                                    {searchResults.matches.length > searchResults.shown.length && (
                                        <p className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100">
                                            Showing the first {searchResults.shown.length} of {searchResults.matches.length}. Keep typing to narrow it down.
                                        </p>
                                    )}
                                </>
                                )}

                                {/* qfupcal-6e3b9d47 — follow-ups, in their own labelled
                                    block so a phone call is never mistaken for a visit in
                                    a list where both appear. */}
                                {followUpSearchResults?.matches.length > 0 && (
                                    <>
                                        <p className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wide text-gray-400 border-t border-gray-100">
                                            {followUpSearchResults.matches.length === 1
                                                ? '1 follow-up'
                                                : `${followUpSearchResults.matches.length} follow-ups`}
                                        </p>
                                        {followUpSearchResults.shown.map(quote => {
                                            const overdue = isFollowUpOverdue(quote.nextFollowUpAt, quote.snoozedUntil, new Date(), companySettings?.timezone);
                                            const snoozed = quote.snoozedUntil && quote.snoozedUntil > new Date();
                                            return (
                                                <button
                                                    key={quote.id}
                                                    type="button"
                                                    onClick={() => handleFollowUpResultClick(quote)}
                                                    className="w-full text-left px-3 py-2 hover:bg-gray-50 border-t border-gray-100"
                                                >
                                                    <span className="block text-sm text-gray-800 truncate">
                                                        <PhoneCall size={12} className="inline-block mr-1 -mt-0.5 text-gray-400" />
                                                        <span className="font-semibold">{quote.quoteNumber || '—'}</span>
                                                        {' · '}{quote.customerDetails?.name || quote.customerName || 'No customer'}
                                                    </span>
                                                    <span className={`block text-xs ${overdue ? 'text-amber-700' : 'text-gray-400'}`}>
                                                        {/* Same words the estimate page uses, so overdue reads
                                                            the same in both places. */}
                                                        {snoozed ? 'Snoozed until' : overdue ? 'Was due' : 'Next'}
                                                        {' '}{format(quote.nextFollowUpAt, 'EEE d MMM yyyy')}
                                                        {quote.followUpHasTime ? `, ${format(quote.nextFollowUpAt, 'h:mm a')}` : ''}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                        {followUpSearchResults.matches.length > followUpSearchResults.shown.length && (
                                            <p className="px-3 py-2 text-xs text-gray-400 border-t border-gray-100">
                                                Showing the first {followUpSearchResults.shown.length} of {followUpSearchResults.matches.length}.
                                            </p>
                                        )}
                                    </>
                                )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white p-2 md:p-4 rounded-lg shadow-md h-[calc(100vh-140px)] min-h-[500px] overflow-x-auto w-full min-w-0">
                {/* FIX: Removed hardcoded min-w-[700px] on mobile to allow agenda view to perfectly fit the screen */}
                {/* calres-7f2b9d41-layout — the scroll container above is the ONLY horizontal
                    scroller, so headers, arrival-window lane and time grid move as one.
                    spatchy-res-day lets .rbc-calendar size to its columns instead of
                    capping at 100% (a parent cannot be selected from the view's class). */}
                <div className={`h-full min-w-[100%] md:min-w-[700px]${view === 'day' && dayResources ? ' spatchy-res-day' : ''}`}>
                <style>{CALENDAR_CSS}</style>
                <DnDCalendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    // calwin-8d4f1b52 — the lane. An untimed entry leaves the time grid and
                    // stacks in the all-day row (TimeGrid.js:285). It is labelled "Arrival
                    // window" rather than "Any time" because the contents are overwhelmingly
                    // specific — 199 of 209 windowed jobs in the busiest org are Morning — and
                    // calling a Morning job "any time" would be the same untruth as a clock
                    // time. The label itself comes from components.timeGutterHeader, NOT from
                    // messages.allDay; see ArrivalWindowGutterLabel for why.
                    // allDayMaxRows defaults to Infinity, which would let one day grow the
                    // lane tall enough to push the grid off screen; 10 is sized from the
                    // census (302 occupied day/column cells in production, p99 of 7, exactly
                    // one cell above 8 rows and that one is bulk-created test data), so a
                    // normal day shows every entry with no "+N more".
                    allDayAccessor={(event) => !!event.isUntimed}
                    allDayMaxRows={10}
                    components={calendarComponents}
                    // calpol-9c4e7b23 — month's visible string drops the job title
                    // (founder decision D4-1), so hover has to carry it. tooltipAccessor
                    // defaults to 'title'; this falls back to exactly that for every
                    // event that sets no `tooltip`, which is every event outside month
                    // view — so week, day and agenda hover is byte-unchanged.
                    tooltipAccessor={(event) => event.tooltip || event.title}
                    // calmore-2d8f4b17 — make "+N more" open a popover instead of doing nothing.
                    // The default is `popup: false` + `doShowMoreDrillDown: true`, which drills
                    // down to the DAY view — so in day view it navigated to the day already on
                    // screen and nothing happened (Month.js:173, TimeGrid.js:87). One prop fixes
                    // all three views identically, because Month and TimeGrid both check `popup`
                    // first and take the same overlay branch.
                    // What it gives, all verified in the library rather than rebuilt:
                    //  • the list is that COLUMN'S lane only — the overlay is fed the same
                    //    per-resource event array the row was rendered from, so one person's
                    //    "+N more" never shows another person's jobs;
                    //  • in seam 2's order, because getEventsForSlot filters segments in array
                    //    order (DateSlotMetrics.js:60-66) and we own that order;
                    //  • each entry opens the job through the SAME onSelectEvent path as a click
                    //    on the calendar (Popup passes onSelect through to EventCell);
                    //  • closing on click-outside AND on Escape, because PopOverlay sets
                    //    rootClose and react-overlays' useRootClose listens for keyup 27.
                    // `doShowMoreDrillDown` is left alone deliberately: with popup on, the
                    // drill-down branch is unreachable, so overriding it would be dead config.
                    popup
                    // calres-7f2b9d41 — SEAM 3: columns per person, DAY VIEW ONLY. Week, month
                    // and agenda are handed no resources and are byte-unchanged (the library
                    // renders every event under one internal group when resources is undefined,
                    // and month ignores resources entirely — Month.js has no reference to them).
                    // resourceIdAccessor/resourceTitleAccessor keep their defaults ('id' /
                    // 'title'), which is why dayResources maps name -> title.
                    // The arrival-window lane splits per column for free: the header renders one
                    // all-day row PER RESOURCE, each with its own allDayMaxRows
                    // (TimeGridHeader.js:171-181), fed from the array-aware grouper — so seam
                    // 2's order and its 10-row cap now apply per column with no extra work.
                    resources={view === 'day' ? (dayResources || undefined) : undefined}
                    onSelectEvent={handleSelectEvent}
                    view={view}
                    date={date}
                    length={agendaLength}
                    onView={handleViewChange}
                    onNavigate={handleNavigate}
                    onEventDrop={handleJobMove}
                    onEventResize={handleJobResize}
                    resizable
                    selectable
                    onSelectSlot={handleSelectSlot}
                    eventPropGetter={eventStyleGetter}
                    // calwin-8d4f1b52-fix — finished work is not reschedulable. The
                    // isFinishedEntry guard is placed FIRST so it wins over every other
                    // clause, including the recurring/projected ones below.
                    // qfupcal-6e3b9d47 — the leading clause refuses quote follow-ups.
                    // This and resizableAccessor below are THE two refusal points: with
                    // both closed, planReassign / executeReassign / executeMoveWithScope
                    // / handleJobMove are unreachable for a follow-up, so they get no
                    // guards of their own. Guarding them would imply a live path that
                    // does not exist. Moving the date is a deliberate act on the
                    // estimate (Log follow-up / Snooze), not a drag on a calendar.
                    draggableAccessor={(event) => !isQuoteFollowUpEntry(event) && !isFinishedEntry(event) && can(currentUserProfile, PERMISSIONS.MANAGE_JOBS) && (event.isCrewAppearance || !event.isProjected || !!(event.resource?.recurringJobId) || event.resource?.recurrence?.frequency !== 'Never')}
                    // calwin-8d4f1b52 — a WINDOW job is no longer resizable, because resizing
                    // means something different in the lane than it did in the grid. In the
                    // grid a resize changed endAt, i.e. the job's duration — a real field the
                    // editor owns. In the all-day row a resize stretches the entry across
                    // DAYS, which would write a multi-day endAt while leaving arrivalWindow
                    // saying "Morning" and isAllDay/durationDays unset: a third inconsistent
                    // shape, created by a gesture that looks like a nudge. Duration stays
                    // editable where it always was, in the job editor.
                    // Time Off and multi-day jobs stay resizable — they were already in this
                    // row before this seam and stretching them across days is what they mean.
                    // Gated on isUntimed AND the window mode, not on the mode alone: a crew
                    // appearance can belong to a job whose top-level mode is 'window' while
                    // the appearance itself is a real timed block in the grid (isUntimed
                    // false), and that must stay resizable. Time Off carries a null mode, and
                    // multi-day jobs have their window forced null on save
                    // (JobFormModal.js:2307-2309), so both fall through unaffected.
                    resizableAccessor={(event) => !isQuoteFollowUpEntry(event) && !isFinishedEntry(event) && can(currentUserProfile, PERMISSIONS.MANAGE_JOBS) && (event.isCrewAppearance || !event.isProjected) && !(event.isUntimed && event.resource?.schedulingMode === 'window')}
                />

                {/* calres3b-6e4a2f81 — reassign confirmation. ONE dialog per gesture:
                    it carries the reassign, the "this visit only" fact for a recurring
                    job (D3b-3, so no second scope prompt), any hidden assignees, and
                    the who-to-replace choice when the job sits in several columns. */}
                {pendingReassign && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                        <div className="bg-white rounded-xl p-6 shadow-2xl w-96">
                            {/* calclar-5e2a8d14 — every word below comes from buildReassignDialog:
                                names only, each option states its result, nothing says "everyone else". */}
                            <h3 className="text-lg font-bold text-gray-800 mb-2">{pendingReassign.title}</h3>
                            {(pendingReassign.lines || []).map(line => (
                                <p key={line} className="text-sm text-gray-500 mb-2">{line}</p>
                            ))}
                            {pendingReassign.thisVisitOnly && (
                                <p className="text-sm text-gray-500 mb-2">
                                    This is a repeating job — the change applies to <span className="font-semibold">this visit only</span>.
                                </p>
                            )}

                            <div className="flex flex-col gap-2 mt-4">
                                {(pendingReassign.options || []).map(opt => (
                                    <button
                                        key={opt.label}
                                        onClick={() => executeReassign(pendingReassign, opt.removeUid, opt.addUid)}
                                        className="w-full text-left px-4 py-3 rounded-lg hover:bg-blue-50 border border-gray-200 hover:border-blue-300 transition-colors"
                                    >
                                        <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">{opt.sub}</p>
                                    </button>
                                ))}
                                <button
                                    onClick={() => setPendingReassign(null)}
                                    className="w-full text-center px-4 py-2 text-sm text-gray-500 hover:text-gray-700 mt-1"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* calclar-5e2a8d14 — after a reassign drop: who is on the job now, and a way to show
                    anyone the Team filter hides (so a job that "vanished" visibly hasn't lost anyone). */}
                {dropNotice && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] max-w-md w-[calc(100%-2rem)] bg-gray-900 text-white rounded-lg shadow-2xl px-4 py-3 flex items-start gap-3" role="status">
                        <div className="flex-1 text-sm">
                            <p>{dropNotice.text}</p>
                            {dropNotice.show.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {dropNotice.show.map(p => (
                                        <button
                                            key={p.uid}
                                            onClick={() => {
                                                if (!selectedEmployees.includes(p.uid)) onEmployeeFilterChange(p.uid);
                                                setDropNotice(prev => (prev ? { ...prev, show: prev.show.filter(x => x.uid !== p.uid) } : prev));
                                            }}
                                            className="px-3 py-1 rounded-md bg-white text-gray-900 text-xs font-semibold hover:bg-gray-100"
                                        >
                                            Show {p.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button onClick={() => setDropNotice(null)} className="text-gray-400 hover:text-white text-sm" aria-label="Dismiss">✕</button>
                    </div>
                )}

                {/* Recurring move scope modal */}
                {pendingMove && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                        <div className="bg-white rounded-xl p-6 shadow-2xl w-80">
                            <h3 className="text-lg font-bold text-gray-800 mb-2">Move recurring event</h3>
                            <p className="text-sm text-gray-500 mb-5">
                                Moving to {new Date(pendingMove.start).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}.
                                Which events should be moved?
                            </p>
                            <div className="flex flex-col gap-2">
                                <button
                                    onClick={() => executeMoveWithScope(pendingMove.event, pendingMove.start, pendingMove.end, 'only_this')}
                                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-blue-50 border border-gray-200 hover:border-blue-300 transition-colors"
                                >
                                    <p className="text-sm font-semibold text-gray-800">This event only</p>
                                    <p className="text-xs text-gray-400 mt-0.5">Move just this occurrence</p>
                                </button>
                                <button
                                    onClick={() => executeMoveWithScope(pendingMove.event, pendingMove.start, pendingMove.end, 'all')}
                                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-blue-50 border border-gray-200 hover:border-blue-300 transition-colors"
                                >
                                    <p className="text-sm font-semibold text-gray-800">All events</p>
                                    <p className="text-xs text-gray-400 mt-0.5">Move the entire recurring series</p>
                                </button>
                                <button
                                    onClick={() => setPendingMove(null)}
                                    className="w-full text-center px-4 py-2 text-sm text-gray-500 hover:text-gray-700 mt-1"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
};

export default CalendarView;